export interface TreatmentFAQ {
  question: string;
  answer: string;
}

export interface TreatmentPricing {
  name: string;
  price: string;
  note?: string;
}

export interface Treatment {
  slug: string;
  title: string;
  metaTitle: string;
  metaDescription: string;
  heroSubtitle: string;
  placeholder: string;
  overview: string;
  /**
   * Answer-first paragraph for LLM answer engines and featured snippets.
   * Shown directly under the hero. 50-90 words, factual, specific to
   * Christchurch where applicable.
   */
  quickAnswer?: string;
  /**
   * Four-item quick-facts strip (change list item 12): the four questions
   * every researcher asks first. Populate ONLY with figures confirmed
   * against the clinical protocol and consistent with the page body; a
   * treatment without confirmed figures renders no strip.
   */
  quickFacts?: {
    time: string;
    comfort: string;
    downtime: string;
    lasts: string;
  };
  content: {
    heading: string;
    body: string;
  }[];
  pricing: TreatmentPricing[];
  faqs: TreatmentFAQ[];
  whyChoose: string[];
  ctaText: string;
}

export const treatments: Treatment[] = [
  {
    slug: "silk-protocol",
    title: "The Silk Protocol",
    metaTitle: "The Silk Protocol Christchurch | Signature Skin Restoration",
    metaDescription: "The Silk Protocol is our signature, fully bespoke restoration plan in Christchurch: deep-structure lifting, dermal tightening and precision refinement, sequenced to address every layer of facial ageing. Nurse-led, no surgery, no downtime.",
    heroSubtitle: "The Complete Lift Protocol",
    placeholder: "[PLACEHOLDER: Silk Protocol consultation and treatment planning]",
    overview: "The Silk Protocol is our signature multi-modality restoration plan: not a single treatment, but a sequenced combination chosen for your face. It works at every layer at once, lifting deep structure, firming the dermis and refining the surface, to restore definition and freshness without surgery, general anaesthetic, or downtime.",
    quickAnswer:
      "The Silk Protocol is Silk Clinical Aesthetics' signature, fully bespoke restoration plan in Christchurch. Rather than one device, it sequences deep-structure lifting with Ultraformer MPT HIFU, volumetric dermal tightening with Volnewmer RF, and, where indicated, precision injectables or threads. Because the face ages across several layers, treating them together restores lift and firmness that a single treatment cannot. Every plan is built by a registered nurse at consultation, staged across visits, with no surgery and no downtime. Results build over three to six months and are maintained periodically.",
    quickFacts: {
      time: "By plan, staged across visits",
      comfort: "No anaesthetic; warmth and light prickling",
      downtime: "None",
      lasts: "1 to 2 years, with maintenance",
    },
    content: [
      {
        heading: "What is the Silk Protocol?",
        body: `The Silk Protocol is the clinic's signature approach to comprehensive facial restoration. It is not a single machine or injection. It is a plan, built for your face, that draws together the treatments best suited to your concerns and sequences them for the strongest combined result.

At its core sit our two flagship devices. Ultraformer MPT HIFU reaches the deep structural layer that gives the face its scaffolding. Volnewmer RF remodels the collagen-rich dermis above it. Where the assessment calls for it, precision injectables and PDO threads refine contour and definition. Silk Clinical is the only clinic in the South Island operating both the Volnewmer and the Ultraformer MPT, which is what makes the full protocol possible here.

The result is an approach that addresses the whole face rather than one feature of it: the closest a considered, non-surgical plan comes to restoring the structure, firmness and freshness that time softens.`,
      },
      {
        heading: "Why treat every layer",
        body: `Skin does not age in one layer, so treating one layer alone only ever solves part of the picture.

Beneath the surface sits the SMAS, the structural sheet a surgeon repositions during a lift. As it descends, the jawline softens and the midface drops. Above it, the dermis loses collagen, and the skin looks loose, crepey or tired. Volume shifts, and fine lines settle in at the surface.

A single treatment works on a single layer. The Silk Protocol is built to work on all of them together: lifting deep structure, firming the dermis, restoring volume where it has been lost, and smoothing the surface. Addressed as one plan, the layers support each other, and the overall result reads as a genuine restoration rather than a single tweak.`,
      },
      {
        heading: "How your protocol is built",
        body: `Every Silk Protocol begins with a full-face assessment. Your registered nurse maps where laxity, descent, volume loss and surface change are showing, and which layers are driving what you see in the mirror.

From that assessment, your nurse selects the combination of treatments that will do the most for your face, and the order and spacing in which to deliver them. Deep-structure lifting and dermal tightening are staged across separate visits so each works at its best, and neither carries downtime, so the plan fits around your life rather than around recovery.

Because the protocol is bespoke, no two are identical. Some faces need the two devices alone; others benefit from injectables or threads woven in for definition. Your nurse will talk you through the plan, the number of visits, realistic outcomes, and the investment before anything begins.`,
      },
      {
        heading: "What the protocol can address",
        body: `The Silk Protocol is designed for clients who want a considered improvement across the whole face rather than a single area.

It addresses skin laxity and softening along the jawline and neck, descent through the midface, loss of firmness and definition, crepey or lax skin texture, fine lines, and volume that has shifted or thinned over time.

It suits clients noticing several signs of ageing at once, and those who want a meaningful result without surgery, incisions, or general anaesthetic. During your consultation, your nurse will confirm your suitability and set clear, honest expectations for what a non-surgical plan can and cannot achieve.`,
      },
      {
        heading: "Results and what to expect",
        body: `Results from the Silk Protocol arrive in two phases. There is an immediate firming as collagen contracts, followed by progressive lifting and tightening over three to six months as the skin builds new collagen in response to treatment.

Because the plan works across several layers, the change tends to read as an overall freshening rather than a single obvious alteration. Outcomes typically hold for one to two years, and most clients return for periodic maintenance to sustain them.

There is no downtime. Mild warmth, redness or slight swelling can follow a session and usually settles within a day. You can return to normal activities immediately. Individual results vary, and your nurse will review your progress across the plan and adjust where needed.`,
      },
    ],
    pricing: [],
    faqs: [
      {
        question: "What is included in the Silk Protocol?",
        answer: "The Silk Protocol is bespoke, so the exact contents are set at your consultation. Most plans combine Ultraformer MPT HIFU for deep-structure lifting with Volnewmer RF for dermal tightening, and may include precision injectables or PDO threads where they add definition. Your registered nurse selects and sequences the treatments that suit your face.",
      },
      {
        question: "How does this compare to surgery?",
        answer: "The Silk Protocol is a non-surgical plan. It works on the same deep structural layer a surgeon targets, but without incisions, general anaesthetic, or the recovery that surgery involves. It is the most comprehensive non-surgical approach we offer, and it suits clients who want a meaningful, natural restoration and are not seeking, or not ready for, a surgical procedure. Your nurse will give you an honest view of what it can achieve for you.",
      },
      {
        question: "How many visits does it take?",
        answer: "That depends on your plan. The device treatments are usually staged across separate visits so each performs at its best, and maintenance is periodic thereafter. Your nurse will map the number and spacing of visits at your consultation.",
      },
      {
        question: "Is there any downtime?",
        answer: "No. Every treatment in the protocol is non-invasive with no downtime. Some clients notice mild warmth, redness or slight swelling that settles within a day. You can return to normal activities immediately.",
      },
      {
        question: "When will I see results, and how long do they last?",
        answer: "You will notice an immediate firming, with progressive lifting and tightening developing over three to six months as new collagen forms. Results typically last one to two years, and most clients return for periodic maintenance to sustain them. Individual results vary.",
      },
      {
        question: "How is the Silk Protocol priced?",
        answer: "Because it is built for your face from the treatments it combines, the Silk Protocol is priced at consultation once your plan is confirmed. Your nurse will set out the full investment, and the number of visits, before anything begins.",
      },
    ],
    whyChoose: [
      "The only clinic in the South Island operating both the Volnewmer and the Ultraformer MPT, the two devices the protocol is built around.",
      "A plan built for every layer of your face, not a single treatment applied to a single concern.",
      "Assessed and sequenced by registered nurses, with injectables always in a registered nurse's hands.",
      "Comprehensive restoration without surgery, general anaesthetic, or downtime.",
      "Ongoing review across the plan, with adjustments made to optimise your result.",
    ],
    ctaText: "Silk Protocol",
  },
  {
    slug: "volnewmer-rf-skin-tightening",
    title: "Volnewmer RF Skin Tightening",
    metaTitle: "Volnewmer Christchurch | South Island's Only Clinic",
    metaDescription: "Volnewmer RF skin tightening in Christchurch, the only clinic in the South Island offering it. Monopolar radiofrequency for firmer skin, no downtime. From $500.",
    heroSubtitle: "South Island Exclusive",
    placeholder: "[PLACEHOLDER: Volnewmer RF device in treatment room]",
    overview: "Silk Clinical Aesthetics is the only clinic in the South Island offering Volnewmer RF skin tightening technology. This advanced monopolar radiofrequency treatment delivers deep collagen remodelling for visibly tighter, smoother skin without surgery or downtime.",
    quickAnswer:
      "Volnewmer RF skin tightening in Christchurch is exclusive to Silk Clinical Aesthetics, the only South Island clinic with the device (as at September 2026). Monopolar radiofrequency at 6.78MHz heats the collagen-rich layers of the dermis while continuous water cooling protects the skin surface. That does two things: existing collagen fibres contract for a tightening effect you can see from the first treatment, and new collagen forms over the months that follow. Sessions take approximately 30 to 90 minutes depending on the area treated, need no anaesthetic or numbing cream, and have no downtime, so you return to normal activities the same day. Results build over 3 to 6 months and typically last 12 to 24 months. Pricing starts from $500 for the eye area, with full face $2,500 and face and neck $3,000.",
    quickFacts: {
      time: "30 to 90 minutes, by area",
      comfort: "No anaesthetic needed",
      downtime: "None",
      lasts: "12 to 24 months",
    },
    content: [
      {
        heading: "What is Volnewmer?",
        body: `Volnewmer is an advanced monopolar radiofrequency (RF) treatment device that represents the next generation of non-surgical skin tightening technology. Operating at a frequency of 6.78MHz, Volnewmer delivers controlled RF energy deep into the skin's collagen-rich layers to stimulate natural collagen production and tissue remodelling.

Manufactured by Classys Inc. in South Korea, Volnewmer is marketed as EVERESSE in the United States market and is cleared in the US and approved in Korea. The device runs at a maximum output of 115W, with four interchangeable tips (0.25, 3.0, 4.0 and 16cm2), dual handpieces and continuous water cooling.

At Silk Clinical Aesthetics, we are the only clinic in the South Island offering Volnewmer (as at September 2026), giving Canterbury and wider South Island clients access to a treatment previously only available in major centres.`,
      },
      {
        heading: "How Does Volnewmer Work?",
        body: `Volnewmer uses monopolar RF energy to heat the deep collagen-rich layers of your skin, specifically targeting the papillary and reticular dermis. This controlled heating triggers two key biological responses.

First, the immediate contraction of existing collagen fibres, which produces a visible tightening effect from your very first treatment. Second, the stimulation of long-term collagen remodelling and regeneration, meaning your results continue to improve over the weeks and months following treatment.

What makes Volnewmer particularly advanced is its proprietary technology suite. The device features Hidden Edge Technology, which prevents energy clustering at electrode edges, a common issue with older RF devices that can lead to uneven results or discomfort. Volnewmer delivers a single energy pulse per REP (rather than multi-pulsation), providing more consistent and predictable energy delivery.

Patient comfort is managed through a sophisticated 4-step vibration and cooling system. Continuous water cooling protects the epidermis (outer skin layer) during treatment, allowing the RF energy to target deeper tissues without risking surface damage.

The device offers four interchangeable tip options designed for different treatment areas across both face and body. A tilting tip head adapts to the natural curvature of your skin, ensuring consistent energy delivery even on contoured areas like the jawline or neck. Unlike some RF devices, Volnewmer has no fixed time limit on consumables, allowing your treating clinician to customise treatment protocols to your specific needs.`,
      },
      {
        heading: "Who Volnewmer Suits, and Who It Does Not",
        body: `Volnewmer may suit mild to moderate laxity of the face, eyes or neck, where there is still enough tissue quality for collagen remodelling to work with.

It does not suit laxity that needs surgery. Energy-based devices are adjuncts, not a replacement for a facelift, and a consultation that tells you otherwise is selling rather than assessing. If surgery is the honest answer for what you want changed, we will say so.

If your weight is still changing, we will usually suggest waiting. Rapid weight loss can deflate the midface and increase laxity, which changes what the device would be treating and can make an early result look wrong within months.

Suitability is decided at consultation, on your face, not from this page.`,
      },
      {
        heading: "What Can Volnewmer Treat?",
        body: `Volnewmer is a versatile treatment suitable for a wide range of concerns across both face and body.

On the face, Volnewmer effectively treats skin laxity and sagging, fine lines and wrinkles, volume loss, and overall skin texture and tone. It is particularly effective for clients noticing early to moderate signs of facial ageing who want to restore firmness without surgery.

For body treatments, Volnewmer can address skin laxity on the arms, abdomen, knees, thighs, glutes, and neck. This makes it an excellent option for clients experiencing skin laxity after weight loss, pregnancy, or simply as part of the natural ageing process.

Your registered nurse will assess your specific concerns during a consultation and recommend whether Volnewmer alone, or in combination with other treatments, will deliver the best outcome for your goals.`,
      },
      {
        heading: "What to Expect During Your Treatment",
        body: `Volnewmer is a non-invasive treatment with no downtime required. Session length depends on the area treated: allow approximately 30 minutes for a single area such as the eyes, and up to 90 minutes for combined face and neck treatments. Your nurse will confirm timing for your plan at consultation.

During treatment, you will feel a warming sensation as the RF energy is delivered to the deeper skin layers. The integrated cooling and vibration system keeps the treatment comfortable for most clients. Many describe the experience as a warm massage.

Your treating clinician will select the appropriate tip for your treatment area and customise the energy settings based on your skin condition, concerns, and tolerance. The tilting tip head ensures consistent contact and energy delivery across the treatment area.

No anaesthesia or numbing cream is required for Volnewmer treatments.`,
      },
      {
        heading: "Results and Recovery",
        body: `One of the significant advantages of Volnewmer is the combination of immediate and progressive results.

Immediately following your treatment, you may notice a skin smoothing effect as the collagen fibres contract. This gives a visible tightening that many clients appreciate straight away.

The more significant results develop over three to six months as your body produces new collagen in response to the treatment. This collagen remodelling process is gradual, meaning your results look natural and progressive rather than sudden.

Results typically last 12 to 24 months depending on your skin condition, lifestyle, and the natural ageing process. Individual results vary. Many clients choose to have maintenance treatments to sustain their results over time.

In terms of recovery, most clients experience minimal side effects. Minor swelling, redness or warmth is possible and typically subsides within 24 hours. You can return to your normal activities immediately after treatment.

No energy device is free of risk. Monopolar radiofrequency as a class has recorded burns and, rarely, scarring. Contact the clinic if you blister.`,
      },
    ],
    pricing: [
      { name: "Eye Treatment", price: "$500" },
      { name: "Full Face", price: "$2,500" },
      { name: "Full Neck", price: "$1,500" },
      { name: "Face and Neck", price: "$3,000" },
      { name: "Arms", price: "$2,500" },
      { name: "Abdomen", price: "$2,500" },
      { name: "Knees, Thighs, Glutes", price: "$2,500" },
    ],
    faqs: [
      {
        question: "How does Volnewmer compare to Thermage?",
        answer: "Both Volnewmer and Thermage are monopolar RF devices, but Volnewmer represents the next generation of the technology. Key differences include Volnewmer's Hidden Edge Technology for more even energy distribution, its 4-step cooling and vibration comfort system, and the absence of fixed time limits on consumable tips, allowing your clinician to fully customise each treatment. Many practitioners consider Volnewmer to deliver more consistent results with improved patient comfort compared to earlier monopolar RF systems.",
      },
      {
        question: "Is Volnewmer painful?",
        answer: "Most clients find Volnewmer very comfortable. The treatment includes a 4-step vibration and cooling system that manages comfort throughout. You will feel a warming sensation in the treatment area, which most people describe as similar to a warm massage. No anaesthesia or numbing cream is required.",
      },
      {
        question: "How many Volnewmer treatments do I need?",
        answer: "Many clients see noticeable results after a single treatment. However, the optimal number of sessions depends on your specific concerns, skin condition, and goals. During your consultation, your registered nurse will recommend a personalised treatment plan. Some clients benefit from a series of treatments for more significant results, while others maintain results with periodic single sessions.",
      },
      {
        question: "How long do Volnewmer results last?",
        answer: "Volnewmer results typically last 12 to 24 months. You will see an immediate tightening effect after treatment, with progressive improvement continuing over three to six months as new collagen forms. The longevity of results depends on individual factors including skin condition, lifestyle, and the natural ageing process. Many clients choose periodic maintenance treatments to sustain their results.",
      },
      {
        question: "Is there any downtime after Volnewmer?",
        answer: "No. Volnewmer is a non-invasive treatment with no downtime. You can return to your normal activities immediately. Some clients may experience minor redness or mild swelling, which typically resolves within 24 hours.",
      },
      {
        question: "Can Volnewmer be combined with other treatments?",
        answer: "Yes. Volnewmer works well as part of a combined treatment plan. It can be paired with HIFU (Ultraformer MPT), cosmetic injectables, or other skin rejuvenation treatments to address multiple concerns. Your nurse will advise on the best combination and timing for your goals.",
      },
      {
        question: "Who is a good candidate for Volnewmer?",
        answer: "Volnewmer is suitable for adults experiencing skin laxity, fine lines, or loss of firmness on the face or body. It is particularly effective for those with early to moderate signs of ageing who want to improve skin tightness without surgery. During your consultation, your nurse will assess your suitability and discuss realistic expectations.",
      },
      {
        question: "What should I ask at my Volnewmer consultation?",
        answer: "Ask who will physically deliver the pulses on the day, how many pulses are planned for your face, and what this treatment cannot achieve for you. Ask when we would recommend HIFU or surgery instead. Ask what maintenance interval we use and what that is based on. And ask for the total if the neck is included, so the quote you leave with is the quote you pay. A consultation that cannot answer those is not an assessment.",
      },
      {
        question: "Is Volnewmer suitable if my weight is still changing?",
        answer: "Usually we will suggest waiting. Rapid weight loss can deflate the midface and increase laxity, which changes what the device would be treating, and a result achieved mid-change can look wrong within months. If your weight has been stable and the laxity is what is bothering you, that is a better starting point. This is assessed at consultation.",
      },
      {
        question: "Why is Silk Clinical the only Volnewmer clinic in the South Island?",
        answer: "Silk Clinical Aesthetics brought Volnewmer to the South Island on the strength of our clinical standards, nursing expertise, and commitment to advanced aesthetic technology. Our team is trained on the device and keeps that training current.",
      },
    ],
    whyChoose: [
      "The only Volnewmer clinic in the South Island, giving you exclusive access to this technology without travelling to Auckland or overseas.",
      "Performed by our trained clinical team with specialist training on the Volnewmer device, at a nurse-led clinic.",
      "Comprehensive consultation to assess your suitability and create a personalised treatment plan.",
      "A clinical environment focused on your safety, comfort, and results.",
      "Ongoing support and follow-up care to track your progress and optimise your outcomes.",
    ],
    ctaText: "Volnewmer RF Skin Tightening",
  },
  {
    slug: "ultraformer-mpt-hifu",
    title: "Ultraformer MPT HIFU",
    metaTitle: "Ultraformer MPT Christchurch | HIFU Skin Lifting",
    metaDescription: "Ultraformer MPT HIFU in central Christchurch: non-surgical lifting and tightening for the face, jaw and neck at a nurse-led clinic. Collagen-building results, no downtime. Book now.",
    heroSubtitle: "Latest Generation HIFU",
    placeholder: "[PLACEHOLDER: Ultraformer MPT device]",
    overview: "Silk Clinical operates the latest Ultraformer MPT in Christchurch, featuring the most advanced HIFU technology available. This fourth-generation device delivers non-surgical face lifting and skin tightening with faster treatments and finer precision than any previous HIFU system.",
    quickAnswer:
      "Ultraformer MPT HIFU at Silk Clinical Aesthetics is the latest-generation high-intensity focused ultrasound device in Christchurch. It delivers non-surgical lifting and tightening of face, neck, and jawline by stimulating deep collagen at three target depths. A full face treatment takes 30 to 60 minutes with no downtime. Visible lifting builds over 3 to 6 months and typically lasts one to two years. Full face including neck $2,500.",
    quickFacts: {
      time: "30 to 60 minutes, full face",
      comfort: "No anaesthetic; mild prickling",
      downtime: "None",
      lasts: "1 to 2 years",
    },
    content: [
      {
        heading: "What is Ultraformer MPT?",
        body: `Ultraformer MPT is the fourth generation in the Ultraformer series and represents the most advanced HIFU (High-Intensity Focused Ultrasound) device available today. The MPT designation stands for Micro-Pulsed Technology, which delivers Thermal Coagulation Points (TCPs) that are 25 times finer than previous generation devices.

Manufactured by Classys Inc. in South Korea and distributed in New Zealand by Cryomed Aesthetics (EBOS Group), the Ultraformer is the world's most widely used HIFU device, trusted in over 60 countries. That position is the manufacturer's, confirmed with Paul Larsen at EBOS, the New Zealand distributor, in August 2026. The MPT model uses MMFU (Micro and Macro Focused Ultrasound) technology, giving practitioners unprecedented precision in targeting different tissue depths.

At Silk Clinical Aesthetics, we have invested in this technology because we believe our Christchurch clients deserve access to the same advanced treatments available in leading clinics worldwide.`,
      },
      {
        heading: "How Does HIFU Work?",
        body: `HIFU technology works by focusing ultrasound waves at specific depth points beneath the skin's surface, similar to how a magnifying glass concentrates light. At these focal points, the ultrasound energy creates Thermal Coagulation Points (TCPs), where tissue temperatures exceed 60 degrees Celsius.

This controlled heating triggers protein denaturation and a carefully managed tissue coagulation response. Importantly, HIFU can reach the SMAS layer (Superficial Musculoaponeurotic System), the same deep structural layer that surgeons target during a traditional facelift, all without any incisions or surface damage.

The body's natural wound healing response then takes over. Blood coagulation, wound contraction, and the formation of tighter, stronger new collagen all contribute to a progressive lifting and tightening effect that develops over the weeks and months following treatment.

The Ultraformer MPT offers several treatment modes. Normal Mode delivers energy in a precise dot pattern. Micro Pulse (MP) Mode provides broader heating across the treatment area and is 2.5 times faster than normal mode. Circular Mode uses a specialised 360-degree handpiece for specific treatment applications. The Ultra Booster Handpiece drives targeted serums into the skin, needle-free, including our Salmon DNA (polynucleotide) booster for regeneration and skin quality, for enhanced tightening, brightening, and plumping effects.`,
      },
      {
        heading: "Equipment and Capabilities",
        body: `The Ultraformer MPT system includes three handpieces (two MMFU handpieces plus one circular 360-degree handpiece) and ten interchangeable cartridges, providing exceptional versatility.

Seven depth options are available: 1.5mm, 2.0mm, 3.0mm, 4.5mm, 6mm, 9mm, and 13mm. This range allows your treating clinician to precisely target different tissue layers, from the superficial dermis through to deep subcutaneous tissue and the SMAS layer.

The ergonomic handpiece design is lighter and slimmer than previous models, with two trigger types for continuous treatment delivery. This translates directly into a better treatment experience for clients, with treatments running up to 2.5 times faster than previous generation HIFU devices.`,
      },
      {
        heading: "What Can Ultraformer MPT Treat?",
        body: `The Ultraformer MPT is remarkably versatile. On the face, it effectively treats skin laxity and sagging, fine lines and wrinkles, droopy eyelids (using specialised cartridges), facial contouring, jawline definition, and neck tightening. It is also effective for improving dull and uneven skin tone.

For body treatments, the deeper cartridges enable body contouring through subcutaneous ultrasound targeting, which can assist with localised fat reduction.

One of the unique advantages of this system is the specialised eyelid cartridges, which allow treatment of delicate areas that many other devices cannot safely reach. This makes it an excellent option for clients concerned about hooding or laxity around the eye area.`,
      },
      {
        heading: "What to Expect During Your Treatment",
        body: `Ultraformer MPT is a non-invasive treatment that requires no anaesthesia. Most clients describe a mild prickling or tingling sensation during treatment, which is well tolerated.

Treatment times vary depending on the areas being addressed, but the MPT technology means sessions are significantly faster than with previous HIFU devices, often taking 30 to 60 minutes for a full face treatment.

There is no downtime after an Ultraformer MPT treatment. You can return to your normal activities immediately, making it a convenient option for clients with busy schedules.`,
      },
      {
        heading: "Results and Recovery",
        body: `Results from Ultraformer MPT develop in two phases. An immediate tightening effect is visible straight after treatment as the collagen contracts. This is followed by progressive improvement over three to six months as the body generates new, tighter collagen.

Results typically last one to two years, with some clients experiencing benefits for up to five years depending on their skin condition and lifestyle. For significant impact, we generally recommend two treatments, followed by maintenance sessions every three to six months to sustain your results.

Recovery is minimal. Most clients experience no visible side effects, though some may notice mild redness or slight swelling that resolves quickly.`,
      },
      {
        heading: "Add a Salmon DNA Booster (Polynucleotides)",
        body: `Salmon DNA, also known as polynucleotides or PDRN, is one of the most talked-about regenerative treatments in skin right now, and we offer it here as a booster alongside your Ultraformer treatment or on its own.

Polynucleotides are purified, low molecular weight DNA fragments derived from salmon, chosen because they are highly compatible with human skin. Rather than adding volume or simply hydrating, they prompt your fibroblasts to repair and regenerate: calming inflammation, supporting circulation, and encouraging fresh collagen and elastin. The result is an improvement in skin quality, firmness, and resilience, with a particular reputation for the delicate under-eye area.

At Silk Clinical, the Salmon DNA Booster can be delivered needle-free, using the Ultraformer's Ultra Booster mode to carry the polynucleotide serum into the skin with ultrasound. That pairs naturally with a HIFU lifting treatment in the same visit, structure from the Ultraformer and skin quality from the polynucleotides, or it can be booked on its own. Because it builds skin quality gradually, a short course gives the best result, and your treating clinician will advise on what suits your skin.`,
      },
    ],
    pricing: [
      { name: "Full Face Lift incl Neck", price: "$2,500" },
      { name: "Mid Face Lift", price: "$1,000" },
      { name: "Glow and SMAS Lift", price: "$2,000" },
      { name: "Hollywood Lift", price: "$950" },
      { name: "Salmon DNA Booster", price: "$500" },
      { name: "Lower Face and Jaw Line", price: "$1,000" },
      { name: "Eye Treatment", price: "$800" },
    ],
    faqs: [
      {
        question: "What is HIFU and how does it work?",
        answer: "HIFU stands for High-Intensity Focused Ultrasound. It works by focusing ultrasound energy at precise depths beneath the skin, creating controlled heating points that stimulate collagen production and tissue tightening. The Ultraformer MPT can reach the SMAS layer, the same structural layer targeted in surgical facelifts, without any incisions.",
      },
      {
        question: "How does Ultraformer MPT compare to Ultherapy?",
        answer: "Both are HIFU devices, but the Ultraformer MPT represents the latest generation of the technology. Key advantages include Micro-Pulsed Technology for finer, more precise energy delivery, faster treatment times (2.5 times faster than previous devices), more depth options (seven versus fewer), and specialised cartridges for delicate areas like the eyelids. Many practitioners report improved client comfort with the MPT system.",
      },
      {
        question: "Is HIFU painful?",
        answer: "Most clients experience a mild prickling or tingling sensation during treatment, which is generally well tolerated. No anaesthesia is required. The Ultraformer MPT's Micro-Pulsed Technology delivers energy more gently than earlier HIFU systems, contributing to improved comfort during treatment.",
      },
      {
        question: "How many HIFU treatments do I need?",
        answer: "For significant lifting and tightening, we typically recommend two treatments. After that, maintenance sessions every three to six months help sustain and build on your results. Your nurse will create a personalised treatment plan based on your concerns and goals during your consultation.",
      },
      {
        question: "How long do HIFU results last?",
        answer: "Results typically last one to two years, with some clients seeing benefits for up to five years. The longevity depends on individual factors including your skin condition, age, and lifestyle. Regular maintenance treatments can help extend your results.",
      },
      {
        question: "What areas can Ultraformer MPT treat?",
        answer: "The Ultraformer MPT can treat the full face including forehead, brow, cheeks, jawline, and chin. It also treats the neck, eyelids (using specialised cartridges), and body areas for contouring. The seven available treatment depths make it one of the most versatile HIFU systems available.",
      },
      {
        question: "Can HIFU be combined with RF treatments like Volnewmer?",
        answer: "Yes. HIFU and RF treatments work at different tissue depths and through different mechanisms, making them excellent complementary treatments. Combining Ultraformer MPT with Volnewmer RF can address a broader range of concerns for comprehensive skin tightening and rejuvenation. Your nurse will advise on the optimal combination and timing.",
      },
      {
        question: "What is the difference between medical-grade and salon HIFU?",
        answer: "Medical-grade HIFU devices like the Ultraformer MPT are TGA/FDA-registered, deliver precisely calibrated energy at specific depths, and are operated by trained healthcare professionals. Salon or beauty-grade devices typically operate at lower energy levels, have fewer depth options, and may not reach the SMAS layer. The clinical results, safety profile, and treatment outcomes from medical-grade HIFU are significantly superior.",
      },
      {
        question: "What is the Salmon DNA Booster, and can it be done with Ultraformer?",
        answer: "Salmon DNA, also called polynucleotides or PDRN, is a regenerative skin treatment using purified DNA fragments from salmon that prompt your skin to rebuild its own collagen and elastin. At Silk Clinical it can be delivered needle-free using the Ultraformer's Ultra Booster serum-penetration mode, which makes it a natural pairing with a HIFU lifting treatment in the same visit, or it can be booked on its own. It is especially popular for improving skin quality and the delicate under-eye area. The Salmon DNA Booster is $500.",
      },
    ],
    whyChoose: [
      "The latest Ultraformer MPT technology, representing the most advanced HIFU system available in Christchurch.",
      "Performed by our trained clinical team with specialist HIFU training, at a nurse-led clinic.",
      "Seven treatment depth options and specialised cartridges for precise, tailored treatments.",
      "Faster treatment times with improved comfort compared to previous generation devices.",
      "Thorough consultation and personalised treatment planning for optimal results.",
    ],
    ctaText: "Ultraformer MPT HIFU",
  },
  {
    slug: "anti-wrinkle-injections",
    title: "Anti-Wrinkle Injections",
    // Prescription brand names in the title are signed off (owner, 2026-08-04),
    // so the title keeps them. Trimmed from 65 to 54 characters by dropping the
    // third brand rather than the location: Relfydess still appears in the meta
    // description, the page body and its own journal article.
    metaTitle: "Anti-Wrinkle Injections Christchurch | Botox & Dysport",
    metaDescription: "Anti-wrinkle injections in central Christchurch by registered nurses. Botox, Dysport and Relfydess, natural results, single areas from $160. Book online.",
    heroSubtitle: "Cosmetic Injectables",
    placeholder: "[PLACEHOLDER: Anti-wrinkle consultation]",
    overview: "Anti-wrinkle injections are one of the most popular and effective cosmetic treatments for reducing fine lines and wrinkles. At Silk Clinical Aesthetics, they are performed by registered nurses using Botox, Dysport, and Relfydess, the new ready-to-use liquid neuromodulator, to deliver natural, refreshed results. Your nurse recommends the product best suited to your muscle strength, treatment area, and goals.",
    quickAnswer:
      "Anti-wrinkle injections at Silk Clinical Aesthetics in Christchurch are performed by registered nurses using Botox, Dysport, or Relfydess, a next-generation ready-to-use liquid neuromodulator from Galderma. Treatment takes 15 to 30 minutes with minimal downtime. Results begin to show within 1 to 5 days depending on the product, and typically last 3 to 6 months. Pricing is per unit: Dysport from $6, Botox from $14, and Relfydess from $7, with single areas from $160 and packages from $380.",
    quickFacts: {
      time: "15 to 30 minutes",
      comfort: "Fine needles, mild pinch",
      downtime: "None; avoid exercise 4 hours",
      lasts: "3 to 4 months",
    },
    content: [
      {
        heading: "What Are Anti-Wrinkle Injections?",
        body: "Anti-wrinkle injections use botulinum toxin type A (available as Botox, Dysport, or Relfydess) to temporarily relax the muscles responsible for dynamic wrinkles, the lines that form when you make facial expressions like frowning, squinting, or raising your eyebrows. By gently reducing muscle activity, these treatments smooth existing lines and prevent new ones from forming.",
      },
      {
        heading: "What is Relfydess?",
        body: "Relfydess is a next-generation anti-wrinkle injection and the first ready-to-use liquid neuromodulator of its kind. Made by Galderma, it arrives as a purified liquid that needs no reconstitution, so the dose is precise and consistent from one treatment to the next. In clinical studies it showed a faster onset than earlier toxins, with some people noticing results within one to three days, and longevity of up to six months for frown lines and crow's feet. Relfydess is a prescription medicine, assessed and administered by our registered nurses, and it sits alongside Botox and Dysport as an option your nurse may recommend based on your treatment area and goals.",
      },
      {
        heading: "How Do Anti-Wrinkle Injections Work?",
        body: "Botulinum toxin works by blocking the nerve signals that tell muscles to contract. When injected in small, precise doses into specific facial muscles, it temporarily reduces muscle movement in that area. This allows the overlying skin to relax and smooth out, reducing the appearance of wrinkles. The treatment does not affect sensation, only muscle movement, and when performed by a skilled injector, results look natural rather than frozen.",
      },
      {
        heading: "What Can Anti-Wrinkle Injections Treat?",
        body: "Common treatment areas include frown lines (between the eyebrows), crow's feet (around the eyes), forehead lines, brow lift, gummy smile, puckered chin, and DAO/marionette lines. Anti-wrinkle injections can also treat medical conditions such as hyperhidrosis (excessive sweating) and masseter/jaw slimming for TMJ-related concerns or facial contouring.",
      },
      {
        heading: "What to Expect During Your Treatment",
        body: "Treatment takes approximately 15 to 30 minutes. Your registered nurse will discuss your concerns and goals, assess your facial anatomy, and recommend a treatment plan. The injections use very fine needles and most clients describe minimal discomfort, often comparing it to a slight pinch. No anaesthesia is required.",
      },
      {
        heading: "Results and Recovery",
        body: "Results typically begin to appear within 3 to 5 days, with full effect visible at 10 to 14 days. Results last approximately 3 to 4 months, after which muscle activity gradually returns and retreatment is needed to maintain results. There is no downtime. You may experience minor redness or small bumps at injection sites, which resolve within minutes to hours. We recommend avoiding strenuous exercise and lying flat for 4 hours after treatment.",
      },
    ],
    pricing: [
      { name: "Dysport per unit", price: "$6.00" },
      { name: "Relfydess per unit", price: "$7.00" },
      { name: "Botox per unit", price: "$14.00" },
      { name: "Minimum toxin charge", price: "$160" },
      { name: "2 Areas package", price: "$380", note: "70u Dysport or 28u Botox" },
      { name: "3 Areas package", price: "$600", note: "120u Dysport or 48u Botox" },
      { name: "Relfydess 2 Areas package", price: "$420" },
      { name: "Relfydess 3 Areas package", price: "$660" },
      { name: "Frown", price: "From $260" },
      { name: "Crows Feet", price: "From $260" },
      { name: "Forehead", price: "From $160" },
      { name: "Brow Lift", price: "From $160" },
      { name: "Gummy Smile", price: "From $160" },
      { name: "Puckered Chin", price: "From $160" },
      { name: "DAO / Marionette Lines", price: "From $160" },
      { name: "Hyperhidrosis", price: "$1,300" },
      { name: "Masseter (Jaw Slimming)", price: "$560" },
      { name: "Lip Flip (add-on only)", price: "$100" },
    ],
    faqs: [
      {
        question: "What is the difference between Botox, Dysport and Relfydess?",
        answer: "All three are botulinum toxin type A products that work the same way. The main differences are in unit measurement, diffusion, and formulation. Dysport tends to spread slightly more, which can suit larger areas. Relfydess is the newest option: a ready-to-use liquid that needs no mixing, with a faster onset and a duration of up to six months in clinical studies. Your nurse will recommend the most suitable product for your treatment areas.",
      },
      {
        question: "What is Relfydess, and is it better than Botox?",
        answer: "Relfydess is a next-generation anti-wrinkle injection from Galderma and the first ready-to-use liquid botulinum toxin type A. Because it comes as a purified liquid rather than a powder that is reconstituted, dosing is precise and consistent. Clinical studies show a faster onset than earlier toxins, with some people seeing results within one to three days, and longevity of up to six months. It is not necessarily better for everyone: Botox and Dysport remain excellent choices. Your nurse will talk you through which product suits your muscle strength, treatment area, and goals.",
      },
      {
        question: "How long do anti-wrinkle injections last?",
        answer: "Results typically last 3 to 4 months. Regular treatments may extend the duration over time as the muscles become conditioned to reduced activity.",
      },
      {
        question: "Will I look frozen or unnatural?",
        answer: "Not when treated by an experienced injector. At Silk Clinical, our registered nurses prioritise natural results. The goal is to soften lines and prevent new ones while maintaining your natural facial expressions and movement.",
      },
      {
        question: "Is there any downtime after anti-wrinkle injections?",
        answer: "No. You can return to your normal activities immediately. We recommend avoiding strenuous exercise and lying flat for 4 hours after treatment. Minor redness or small bumps at injection sites resolve within minutes to hours.",
      },
      {
        question: "At what age should I start anti-wrinkle injections?",
        answer: "There is no set age. Some clients start in their mid-20s as a preventative measure, while others begin when they notice lines forming at rest. Your nurse will assess your skin and discuss whether treatment is appropriate for your concerns.",
      },
      {
        question: "How much do anti-wrinkle injections cost in Christchurch?",
        answer: "At Silk Clinical, pricing is per unit: Dysport is $6 per unit, Relfydess $7 per unit, and Botox $14 per unit, with a minimum charge of $160 per visit. Single areas start from $160, and the consultation, treatment and follow-up review are included in that price. Your exact dose, and therefore your total, is confirmed at consultation because dosage varies between individuals. For the full 2026 breakdown, including package pricing and how Christchurch clinic pricing compares, read our journal guide: How Much Does Anti-Wrinkle Treatment Cost in Christchurch in 2026?",
      },
    ],
    whyChoose: [
      "All injections performed by registered nurses with specialist injectable training.",
      "Botox, Dysport and Relfydess available, including the new ready-to-use liquid neuromodulator, with expert guidance on which suits you.",
      "Focus on natural results that enhance your features without looking overdone.",
      "Thorough consultation and facial assessment before any treatment.",
      "Competitive pricing with transparent per-unit and package options.",
    ],
    ctaText: "Anti-Wrinkle Injections",
  },
  {
    slug: "dermal-fillers",
    title: "Dermal Fillers",
    metaTitle: "Dermal Filler Christchurch | Cheeks, Jaw, Chin",
    metaDescription: "Dermal filler in Christchurch for cheeks, jawline, chin and tear trough, by registered nurses. Structural, natural-looking results that last 12 to 18 months. Book online.",
    heroSubtitle: "Volume & Contouring",
    placeholder: "[PLACEHOLDER: Dermal filler consultation]",
    overview: "Dermal fillers restore volume, enhance contours, and soften lines using hyaluronic acid-based products. At Silk Clinical Aesthetics, our registered nurses deliver precise filler treatments for natural, balanced results across cheeks, chin, jawline, tear trough, and more.",
    quickAnswer:
      "Dermal fillers in Christchurch at Silk Clinical Aesthetics are hyaluronic acid injectables performed by registered nurses to restore volume in cheeks, chin, jawline, lips, and tear troughs. Treatment takes 30 to 60 minutes, results are immediate, and last 6 to 18 months depending on the product and area. Pricing starts from $350, with most 1ml treatments $500 to $640.",
    quickFacts: {
      time: "30 to 60 minutes",
      comfort: "Topical anaesthetic available",
      downtime: "Possible bruising or swelling",
      lasts: "6 to 18 months",
    },
    content: [
      {
        heading: "What Are Dermal Fillers?",
        body: "Dermal fillers are injectable treatments that use hyaluronic acid (HA), a substance naturally found in your skin, to restore lost volume, enhance facial contours, and smooth deep lines and folds. Unlike anti-wrinkle injections that relax muscles, fillers physically add volume beneath the skin's surface.",
      },
      {
        heading: "How Do Dermal Fillers Work?",
        body: "Hyaluronic acid fillers are injected beneath the skin at specific depths and locations to add volume, lift tissues, or smooth lines. Different filler consistencies are used for different areas: softer fillers for delicate areas like lips and tear troughs, medium fillers for lines and folds, and thicker fillers for structural areas like cheeks, chin, and jawline. The HA also attracts and holds water, providing hydration to the treated area.",
      },
      {
        heading: "What Can Dermal Fillers Treat?",
        body: "Dermal fillers are versatile and can address cheek volume loss, chin projection and definition, jawline contouring, tear trough hollows, nasolabial folds (smile lines), marionette lines, temple hollowing, and hand rejuvenation. They are also used for lip enhancement (see our dedicated lip filler page).",
      },
      {
        heading: "What to Expect During Your Treatment",
        body: "Treatment times vary from 30 to 60 minutes depending on the areas being treated. Your registered nurse will conduct a thorough facial assessment, discuss your goals, and recommend a treatment plan. Most fillers contain lidocaine (a local anaesthetic) for comfort. Results are visible immediately, though final results are best assessed after any initial swelling subsides over 1 to 2 weeks.",
      },
      {
        heading: "Results and Recovery",
        body: "Results are immediate, with the treated area showing restored volume or improved contour straight away. Minor swelling and bruising are common and typically resolve within 1 to 2 weeks. Results last 6 to 18 months depending on the product used, treatment area, and individual metabolism. Touch-up treatments can maintain your results over time.",
      },
    ],
    pricing: [
      { name: "Soft 1ml", price: "$500" },
      { name: "Medium 1ml", price: "$500" },
      { name: "Thick 1ml", price: "$640" },
      { name: "Lips 0.5ml", price: "$350" },
      { name: "Lips 1ml", price: "$500" },
      { name: "Cheeks/Chin/Jaw 1ml", price: "$640" },
      { name: "Cheeks/Chin/Jaw 2ml", price: "$1,000" },
      { name: "Tear Trough 1ml", price: "$640" },
      { name: "Nasolabial Folds 1ml", price: "$500" },
      { name: "Dissolver (corrective)", price: "$350" },
    ],
    faqs: [
      {
        question: "How long do dermal fillers last?",
        answer: "Results typically last 6 to 18 months depending on the product, treatment area, and your individual metabolism. Thicker fillers used in structural areas like cheeks and jawline tend to last longer than softer products.",
      },
      {
        question: "Are dermal fillers safe?",
        answer: "Hyaluronic acid fillers have an excellent safety profile when administered by qualified practitioners. HA is a naturally occurring substance in the body and can be dissolved with an enzyme (hyaluronidase) if needed. At Silk Clinical, all filler treatments are performed by registered nurses.",
      },
      {
        question: "Will dermal fillers look natural?",
        answer: "When performed by an experienced injector, absolutely. Our nurses focus on restoring balance and proportion rather than creating an overfilled appearance. We take a conservative approach, and it is always possible to add more at a follow-up appointment.",
      },
      {
        question: "What is the recovery time for dermal fillers?",
        answer: "Most clients return to normal activities immediately. Swelling and bruising are common, especially in the lip area, and typically resolve within 1 to 2 weeks. We provide detailed aftercare instructions to minimise side effects.",
      },
      {
        question: "Can dermal fillers be dissolved?",
        answer: "Yes. Hyaluronic acid fillers can be dissolved using an enzyme called hyaluronidase. This provides a safety net and also allows for corrections if needed. We offer a dissolver service at Silk Clinical.",
      },
      {
        question: "Who should not have dermal fillers?",
        answer: "Dermal fillers are not recommended during pregnancy or breastfeeding, or where there is an active skin infection near the treatment area. Some medical conditions and medications also affect suitability. Your nurse takes a full medical history at consultation and will tell you plainly if filler is not the right treatment for you, or not the right treatment yet.",
      },
    ],
    whyChoose: [
      "Registered nurse injectors with specialist filler training and a focus on facial anatomy.",
      "Range of premium hyaluronic acid products for different treatment areas.",
      "Conservative, natural approach with the option to build gradually.",
      "Thorough facial assessment and personalised treatment planning.",
      "Dissolver available for corrections or reversals.",
    ],
    ctaText: "Dermal Fillers",
  },
  {
    slug: "lip-filler",
    title: "Lip Filler",
    metaTitle: "Lip Filler Christchurch | Natural Results",
    metaDescription: "Natural lip filler in Christchurch by registered nurse Shontelle Prasad. Subtle hydration to considered volume, tailored to your face. Book a consultation.",
    heroSubtitle: "Lip Enhancement",
    placeholder: "[PLACEHOLDER: Natural lip filler results]",
    overview: "Lip filler at Silk Clinical Aesthetics is about enhancing your natural lip shape and volume, not creating a one-size-fits-all look. Our registered nurses specialise in tailored lip treatments that range from subtle hydration to more defined enhancement.",
    quickAnswer:
      "Lip filler in Christchurch at Silk Clinical Aesthetics uses hyaluronic acid to enhance lip shape, hydration, and volume. Treatment takes approximately 20 to 30 minutes with minimal downtime. Pricing starts from $350 for 0.5ml or $500 for 1ml. Results are immediate, settle over 2 weeks, and typically last 6 to 12 months. Performed by registered nurses focused on natural, balanced results.",
    quickFacts: {
      time: "20 to 30 minutes",
      comfort: "Numbed thoroughly first",
      downtime: "Swelling settles over 2 weeks",
      lasts: "6 to 12 months",
    },
    content: [
      {
        heading: "What is Lip Filler?",
        body: "Lip filler uses hyaluronic acid-based dermal filler to enhance the volume, shape, and definition of the lips. Whether you want a subtle boost in hydration and fullness or more noticeable enhancement, the treatment is fully customisable to your goals and facial proportions.",
      },
      {
        heading: "How Does Lip Filler Work?",
        body: "Your nurse will inject small amounts of hyaluronic acid filler into precise locations within the lips. By controlling the placement, depth, and volume of filler, we can enhance lip volume, define the lip border (vermillion border), improve symmetry, smooth fine lines around the mouth, and create a more proportionate lip shape relative to your facial features.",
      },
      {
        heading: "What to Expect During Your Treatment",
        body: "Treatment takes approximately 20 to 30 minutes. We apply a topical numbing cream beforehand, and the filler itself contains lidocaine for comfort. Your nurse will mark injection points and discuss the plan before beginning. Most clients find the procedure very manageable.",
      },
      {
        heading: "Results and Recovery",
        body: "Results are visible immediately, though expect swelling for 2 to 5 days (sometimes up to 2 weeks). The final result is best assessed at 2 weeks once all swelling has resolved. Lip filler typically lasts 6 to 12 months. Bruising is possible and usually resolves within a week.",
      },
    ],
    pricing: [
      { name: "Lips 0.5ml", price: "$350" },
      { name: "Lips 1ml", price: "$500" },
    ],
    faqs: [
      {
        question: "How much lip filler do I need?",
        answer: "This depends on your starting point and goals. Many first-time clients start with 0.5ml for a subtle enhancement, while 1ml provides a more noticeable result. Your nurse will recommend the right amount during your consultation.",
      },
      {
        question: "Is lip filler painful?",
        answer: "We apply numbing cream before treatment, and the filler contains lidocaine (anaesthetic). Most clients describe the sensation as a slight pressure or pinch. Comfort levels vary, but the procedure is generally well tolerated.",
      },
      {
        question: "How long does lip filler last?",
        answer: "Lip filler typically lasts 6 to 12 months. The lip area has high movement and blood flow, which can mean filler metabolises faster here than in other facial areas.",
      },
      {
        question: "Will my lip filler look natural?",
        answer: "Our nurses specialise in natural lip enhancement. We assess your facial proportions and existing lip shape to create a result that suits you. We always prefer a conservative approach, as it is easier to add more than to correct overfilling.",
      },
      {
        question: "What if I don't like the result?",
        answer: "Hyaluronic acid lip filler can be dissolved using hyaluronidase enzyme. This provides a complete safety net. However, with careful consultation and a skilled injector, this is rarely needed.",
      },
      {
        question: "How should I prepare for a lip filler appointment?",
        answer: "Arrive with clean skin and no lip products. Bruising risk is lower if you avoid alcohol for 24 hours before treatment; if you take blood-thinning medication or supplements, mention it at consultation rather than stopping anything on your own. Plan around the swelling window: results settle over 2 weeks, so book at least a fortnight ahead of any significant event.",
      },
    ],
    whyChoose: [
      "Registered nurses with specialist lip injection training.",
      "Personalised approach based on your facial proportions and goals.",
      "Natural results, from subtle to statement.",
      "Premium hyaluronic acid fillers with built-in anaesthetic for comfort.",
      "Dissolvable products for complete peace of mind.",
    ],
    ctaText: "Lip Filler",
  },
  {
    slug: "dermal-fillers-for-men",
    title: "Dermal Fillers for Men",
    metaTitle: "Dermal Fillers for Men Christchurch | Jawline & Chin",
    metaDescription: "Dermal fillers for men in Christchurch. Jawline definition, chin projection and facial contouring by registered nurses. Discreet, natural results at Silk Clinical.",
    heroSubtitle: "Male Aesthetics",
    placeholder: "[PLACEHOLDER: Male dermal filler treatment]",
    overview: "An increasing number of men are choosing dermal fillers to enhance jawline definition, chin projection, and overall facial structure. At Silk Clinical Aesthetics, we understand the distinct goals of male clients and tailor our approach to deliver strong, natural-looking results.",
    quickAnswer:
      "Dermal fillers for men in Christchurch at Silk Clinical Aesthetics use hyaluronic acid to strengthen jawline definition, chin projection, and cheek structure. Treatment takes 30 to 60 minutes, results are immediate, and typically last 12 to 18 months in structural areas. Pricing starts from $500 (nasolabial folds), with jawline, chin, and cheek work from $640 per ml. All treatments performed by registered nurses in a discreet clinic environment.",
    quickFacts: {
      time: "30 to 60 minutes",
      comfort: "Topical anaesthetic available",
      downtime: "Most return to work same day",
      lasts: "12 to 18 months",
    },
    content: [
      {
        heading: "What Are Dermal Fillers for Men?",
        body: "The same hyaluronic acid fillers used in female treatments are applied with different techniques and goals for male clients. Male facial anatomy typically has stronger bone structure, wider jawlines, and flatter cheek contours. Our nurses understand these differences and adjust their approach to enhance masculine facial proportions.",
      },
      {
        heading: "Popular Treatment Areas for Men",
        body: "The most requested areas for men include jawline contouring for a stronger, more defined jawline, chin projection and definition, cheek volume to address hollow or gaunt appearance, tear trough treatment for under-eye hollowing, and nasolabial fold reduction. Many male clients also seek treatment to look more rested and refreshed without anyone noticing they have had anything done.",
      },
      {
        heading: "What to Expect",
        body: "The treatment experience is identical to standard dermal filler procedures. Consultation, treatment, and aftercare are tailored to your goals. Treatments take 30 to 60 minutes, and results are immediate. We understand that discretion is important to many male clients, and our clinic environment reflects this.",
      },
      {
        heading: "Results and Recovery",
        body: "Results are visible immediately and typically last 12 to 18 months in the structural areas we treat, such as the jawline, chin, and cheeks. Swelling and bruising are possible but typically mild, and most clients return to work the same day. The focus is on subtle, structural enhancement that improves your overall appearance without looking obvious.",
      },
    ],
    pricing: [
      { name: "Cheeks/Chin/Jaw 1ml", price: "$640" },
      { name: "Cheeks/Chin/Jaw 2ml", price: "$1,000" },
      { name: "Tear Trough 1ml", price: "$640" },
      { name: "Nasolabial Folds 1ml", price: "$500" },
    ],
    faqs: [
      {
        question: "Is it common for men to get dermal fillers?",
        answer: "Yes. Male cosmetic treatments have grown significantly in recent years. Jawline and chin fillers are among the most popular procedures for men. At Silk Clinical, we regularly treat male clients.",
      },
      {
        question: "Will anyone notice I have had filler?",
        answer: "Our approach for male clients focuses on subtle structural enhancement. The goal is to look like a better version of yourself, not to look like you have had work done. Results are natural and proportionate to your facial structure.",
      },
      {
        question: "How is the approach different for men?",
        answer: "Male facial anatomy has different proportions, bone structure, and aesthetic goals compared to female anatomy. Our nurses are trained to enhance masculine features: stronger jawlines, more angular contours, and chin projection, rather than the softer contours typically sought by female clients.",
      },
      {
        question: "How long do results last?",
        answer: "Results typically last 12 to 18 months in structural areas like the jawline and chin. Your nurse will recommend a maintenance schedule to sustain your results.",
      },
      {
        question: "What is the recovery like?",
        answer: "Minimal. Most men return to work the same day. Some swelling or bruising is possible but usually resolves within a few days. We provide detailed aftercare to help you manage any side effects.",
      },
      {
        question: "How much do dermal fillers for men cost?",
        answer: "Structural work on the cheeks, chin, and jawline is $640 for 1ml, $1,000 for 2ml, and $1,550 for 3ml. Tear trough treatment is $640 per ml and nasolabial folds $500 per ml. Jawline and chin work often uses more than 1ml to achieve visible definition; your nurse will give you an exact plan and quote at consultation before anything is booked.",
      },
    ],
    whyChoose: [
      "Nurses trained in male facial anatomy and masculine aesthetic goals.",
      "Focus on strong, natural structural enhancement.",
      "Discreet, professional clinic environment.",
      "Premium fillers selected for structural support and longevity.",
      "Thorough consultation to understand your specific goals.",
    ],
    ctaText: "Dermal Fillers for Men",
  },
  {
    slug: "skin-hydration-injections",
    title: "Skin Hydration Injections",
    metaTitle: "Skin Boosters & Sunekos Christchurch | Injectable Hydration",
    metaDescription: "Skin booster and hydration injections in Christchurch: Sunekos and hyaluronic acid boosters for deep hydration, glow and skin quality, by registered nurses. Book online.",
    heroSubtitle: "Skin Boosters",
    placeholder: "[PLACEHOLDER: Skin hydration treatment]",
    overview: "Skin hydration injections, also known as skin boosters, deliver hyaluronic acid and biorevitalising ingredients directly into the skin for deep hydration, improved texture, and a natural glow from within.",
    quickAnswer:
      "Skin hydration injections in Christchurch at Silk Clinical Aesthetics deliver a thin, spreadable hyaluronic acid into the skin for deep hydration, improved texture, and radiance, without adding volume. Treatment takes 20 to 30 minutes. A two-treatment course is recommended for HA skin boosters ($1,500), or a three-session Sunekos series ($1,800), with maintenance every 3 to 6 months. Improvements develop over 2 to 4 weeks. Performed by registered nurses.",
    quickFacts: {
      time: "20 to 30 minutes",
      comfort: "Topical numbing available",
      downtime: "Small bumps for 24 to 48 hours",
      lasts: "Maintain every 3 to 6 months",
    },
    content: [
      {
        heading: "What Are Skin Hydration Injections?",
        body: "Unlike traditional dermal fillers that add volume, skin boosters deliver a thin, spreadable form of hyaluronic acid into the superficial layers of the skin. This deeply hydrates the skin from within, improving overall quality, luminosity, and elasticity. We also offer Sunekos, which combines hyaluronic acid with amino acids to stimulate your own collagen and elastin production.",
      },
      {
        heading: "How Do They Work?",
        body: "The hyaluronic acid is injected in small deposits across the treatment area, where it attracts and retains moisture. This improves skin hydration, fine lines, texture, and radiance. Sunekos works differently by using a patented combination of hyaluronic acid and amino acids to stimulate the extracellular matrix, encouraging your skin to produce its own collagen and elastin.",
      },
      {
        heading: "How Sunekos Differs from a Standard Skin Booster",
        body: "It helps to separate three things. A dermal filler is a firm, cross-linked hyaluronic acid gel that adds volume where it is placed. A standard HA skin booster deposits soft hyaluronic acid to hydrate the skin and lift its surface quality. Sunekos does something different again: it pairs low molecular weight hyaluronic acid with a patented cluster of six amino acids, the building blocks of collagen and elastin. Rather than only hydrating, it signals your fibroblasts to rebuild the extracellular matrix, the skin's underlying scaffold, so your skin produces new collagen and elastin of its own. That regenerative action is why Sunekos is often chosen for thin, delicate, or crepey skin, including the under-eyes, neck, and décolletage, where volume is not the goal but skin quality is. Results build gradually over a short course rather than appearing all at once.",
      },
      {
        heading: "What to Expect",
        body: "Treatment takes 20 to 30 minutes. Multiple small injections are administered across the treatment area. Most clients find the procedure comfortable, especially with topical numbing. Results develop gradually over the treatment course, with optimal results seen after completing the recommended series.",
      },
      {
        heading: "Results and Recovery",
        body: "You may notice small bumps at injection sites for 24 to 48 hours. Skin quality improvements develop over 2 to 4 weeks. For skin boosters, a two-treatment course is recommended. For Sunekos, an initial series of three sessions is standard, followed by maintenance. Results include improved hydration, smoothed fine lines, enhanced skin tone, and a natural healthy glow.",
      },
    ],
    pricing: [
      { name: "Two Treatment Package", price: "$1,500", note: "2x 2.5ml" },
      { name: "Sunekos Initial Series (3 sessions)", price: "$1,800" },
    ],
    faqs: [
      {
        question: "What is the difference between skin boosters and dermal fillers?",
        answer: "Skin boosters improve skin quality (hydration, texture, glow) without adding volume, while dermal fillers add volume to specific areas. They serve different purposes and are often used together for comprehensive facial rejuvenation.",
      },
      {
        question: "How many treatments do I need?",
        answer: "For HA skin boosters, a two-treatment course is recommended. For Sunekos, an initial series of three sessions is standard. Maintenance treatments are recommended every 3 to 6 months.",
      },
      {
        question: "What areas can be treated?",
        answer: "Skin boosters can be used on the face, neck, decolletage, and hands. They are particularly effective for areas showing early signs of ageing or where skin quality has declined.",
      },
      {
        question: "Is there any downtime?",
        answer: "Small bumps at injection sites may be visible for 24 to 48 hours. Mild redness is possible. Most clients resume normal activities the same day.",
      },
      {
        question: "What is Sunekos, and is it the same as a standard HA booster or filler?",
        answer: "Sunekos is a bio-regenerative injectable that combines low molecular weight hyaluronic acid with a patented cluster of six amino acids. It is not the same as either. A standard HA skin booster mainly hydrates, and a filler adds volume; Sunekos instead supplies both the hyaluronic acid and the amino-acid building blocks your skin needs to regenerate its own collagen, elastin, and extracellular matrix. It is particularly suited to thin or delicate skin, works gradually over a course of sessions, and improves quality and firmness rather than adding volume.",
      },
      {
        question: "Can Sunekos treat under-eye and crepey skin?",
        answer: "Yes. Sunekos is well suited to the delicate under-eye area and other thin, crepey skin such as the neck and décolletage. It does not add volume like a tear-trough filler; instead it improves skin quality and firmness by regenerating collagen and elastin. Your nurse will assess whether Sunekos, filler, or a combination is right for your concern.",
      },
      {
        question: "How much do skin hydration injections cost in Christchurch?",
        answer: "The recommended two-treatment HA skin booster course is $1,500, with maintenance sessions at $800. The Sunekos initial series of three sessions is $1,800, with maintenance at $600. Prices are a guide; your nurse confirms the right product and course for your skin at consultation.",
      },
    ],
    whyChoose: [
      "Premium skin booster products including Sunekos.",
      "Registered nurses with expertise in skin quality treatments.",
      "Personalised treatment plans based on your skin assessment.",
      "Suitable for face, neck, decolletage, and hands.",
      "Natural, gradual improvement in skin quality and hydration.",
    ],
    ctaText: "Skin Hydration Injections",
  },
  {
    slug: "pdo-mono-threads",
    title: "PDO Mono Threads",
    metaTitle: "PDO Mono Threads Christchurch | Collagen Stimulation",
    metaDescription: "PDO mono threads in Christchurch for collagen stimulation and skin tightening. Performed by registered nurses at Silk Clinical Aesthetics. Book your consultation.",
    heroSubtitle: "Collagen Stimulation",
    placeholder: "[PLACEHOLDER: PDO mono thread treatment]",
    overview: "PDO mono threads are fine, absorbable threads inserted beneath the skin to stimulate collagen production and improve skin tightness, texture, and tone. A minimally invasive collagen-stimulating treatment performed by registered nurses at Silk Clinical.",
    quickAnswer:
      "PDO mono threads in Christchurch at Silk Clinical Aesthetics are fine, absorbable threads placed beneath the skin to stimulate your own collagen, improving skin firmness, texture, and tone. Treatment takes 30 to 60 minutes under local anaesthetic. Initial improvement shows within 2 to 4 weeks, with results lasting 12 to 18 months. Pricing starts at $170 for 10 threads, or $650 for 50. Performed by registered nurses.",
    quickFacts: {
      time: "30 to 60 minutes",
      comfort: "Local anaesthetic applied",
      downtime: "Tenderness for 3 to 7 days",
      lasts: "12 to 18 months",
    },
    content: [
      {
        heading: "What Are PDO Mono Threads?",
        body: "PDO (Polydioxanone) mono threads are fine, smooth, absorbable sutures that are placed beneath the skin using thin needles. Unlike lifting threads, mono threads are not designed to physically lift tissue. Instead, they work by stimulating your body's natural collagen production around each thread, resulting in improved skin firmness, texture, and quality over time.",
      },
      {
        heading: "How Do PDO Mono Threads Work?",
        body: "Once inserted, the threads create a controlled healing response. Your body recognises the thread as a foreign material and begins producing collagen around it. Over 4 to 6 months, the PDO material gradually dissolves, but the new collagen structure remains, providing lasting improvement to skin quality and tightness.",
      },
      {
        heading: "What to Expect",
        body: "Treatment takes 30 to 60 minutes depending on the area and number of threads. Local anaesthetic is applied for comfort. The threads are inserted using fine needles, and most clients tolerate the procedure well. Some tenderness and mild swelling may occur for a few days after treatment.",
      },
      {
        heading: "Results and Recovery",
        body: "Initial skin quality improvement is visible within 2 to 4 weeks, with full collagen development over 3 to 6 months. Results last 12 to 18 months. Mild swelling, tenderness, and occasional bruising may occur for 3 to 7 days. Most clients return to normal activities within a day or two.",
      },
    ],
    pricing: [
      { name: "PDO Mono 10 Threads", price: "$170" },
      { name: "PDO Mono 50 Threads", price: "$650" },
    ],
    faqs: [
      {
        question: "What is the difference between mono threads and lifting threads?",
        answer: "Mono threads are smooth and work primarily through collagen stimulation, improving skin quality and mild tightening. Lifting threads have barbs or cogs that physically reposition tissue for a more dramatic lifting effect. They serve different purposes and can be used together.",
      },
      {
        question: "How many threads do I need?",
        answer: "This depends on the treatment area and your goals. Your nurse will recommend the appropriate number during consultation. Packages of 10 and 50 threads are available.",
      },
      {
        question: "Is the treatment painful?",
        answer: "Local anaesthetic is applied before treatment. You may feel some pressure or mild discomfort during thread insertion, but the procedure is generally well tolerated.",
      },
      {
        question: "How long do results last?",
        answer: "Results last approximately 12 to 18 months. The PDO threads dissolve over 4 to 6 months, but the collagen stimulated by the threads persists beyond this point.",
      },
      {
        question: "Can PDO threads be combined with other treatments?",
        answer: "Yes. PDO mono threads work well alongside other treatments such as dermal fillers, anti-wrinkle injections, and skin tightening procedures. Your nurse will advise on optimal combination protocols.",
      },
      {
        question: "What is the downtime after PDO mono threads?",
        answer: "Mild swelling, tenderness, and occasional bruising can occur for 3 to 7 days after treatment. Most clients return to normal activities within a day or two. Initial skin quality improvement shows within 2 to 4 weeks as collagen begins to form, with full development over 3 to 6 months.",
      },
    ],
    whyChoose: [
      "Registered nurses with specialist thread treatment training.",
      "Premium quality PDO threads for safety and efficacy.",
      "Thorough consultation and treatment planning.",
      "Can be combined with other treatments for comprehensive results.",
      "Minimally invasive with minimal downtime.",
    ],
    ctaText: "PDO Mono Threads",
  },
  {
    slug: "lifting-threads",
    title: "Lifting Threads",
    metaTitle: "Lifting Threads Christchurch | Non-Surgical Face Lift",
    metaDescription: "Non-surgical face lift with lifting threads in Christchurch. PDO barbed threads for immediate lifting by registered nurses. Book at Silk Clinical Aesthetics.",
    heroSubtitle: "Non-Surgical Lift",
    placeholder: "[PLACEHOLDER: Lifting thread results]",
    overview: "Lifting threads provide an immediate, visible lift to sagging facial tissue without surgery. Using barbed PDO threads, this treatment physically repositions tissue while simultaneously stimulating collagen production for long-lasting results.",
    quickAnswer:
      "Lifting threads in Christchurch at Silk Clinical Aesthetics use barbed PDO threads to physically reposition mild to moderate facial laxity, with an immediate visible lift and progressive collagen support. Treatment takes approximately 45 to 60 minutes under local anaesthetic. Full results develop over 2 to 3 months and typically last 12 to 24 months. Pricing is $2,000 for eight 18g threads. Performed by registered nurses with advanced thread training.",
    quickFacts: {
      time: "45 to 60 minutes",
      comfort: "Local anaesthetic applied",
      downtime: "Swelling settles in 1 to 2 weeks",
      lasts: "12 to 24 months",
    },
    content: [
      {
        heading: "What Are Lifting Threads?",
        body: "Lifting threads are PDO (Polydioxanone) threads with barbs or cogs that anchor into the tissue, allowing your nurse to physically reposition and lift sagging skin. Unlike smooth mono threads that primarily stimulate collagen, lifting threads provide an immediate mechanical lift combined with ongoing collagen stimulation as the threads dissolve.",
      },
      {
        heading: "How Do Lifting Threads Work?",
        body: "The barbed threads are inserted beneath the skin and anchored at strategic points. The barbs grip the tissue, allowing your nurse to gently reposition it into a lifted position. Over the following weeks and months, collagen forms around the threads, providing structural support that persists even after the threads dissolve. The combination of immediate lift and progressive collagen formation delivers both instant and lasting results.",
      },
      {
        heading: "What to Expect",
        body: "Treatment takes approximately 45 to 60 minutes. Local anaesthetic is applied to the treatment area for comfort. Your nurse will mark the insertion points and desired lift direction before beginning. The procedure involves more significant tissue manipulation than mono threads, and some clients may experience mild discomfort.",
      },
      {
        heading: "Results and Recovery",
        body: "Results are visible immediately after treatment. Some swelling and bruising is normal and typically resolves within 1 to 2 weeks. You may feel tightness or pulling sensation for the first few days. We recommend avoiding strenuous exercise and extreme facial movements for 2 weeks. Full results develop over 2 to 3 months as collagen forms around the threads, and typically last 12 to 24 months.",
      },
    ],
    pricing: [
      { name: "Lifting Threads 18g x 8", price: "$2,000" },
    ],
    faqs: [
      {
        question: "How long do lifting thread results last?",
        answer: "Results typically last 12 to 24 months. The threads dissolve over 6 to 9 months, but the collagen structure they stimulate provides ongoing support beyond this point.",
      },
      {
        question: "Is a thread lift as effective as a surgical facelift?",
        answer: "A thread lift provides a noticeable lift but is not equivalent to a surgical facelift. It is best suited for clients with mild to moderate skin laxity who want improvement without the risks, cost, and recovery time of surgery.",
      },
      {
        question: "What is the recovery time?",
        answer: "Most clients take 3 to 5 days off social activities. Swelling and bruising resolve within 1 to 2 weeks. We recommend avoiding strenuous exercise for 2 weeks.",
      },
      {
        question: "Can lifting threads be combined with other treatments?",
        answer: "Yes. Lifting threads are often combined with anti-wrinkle injections, dermal fillers, or skin tightening treatments for comprehensive facial rejuvenation. Your nurse will advise on timing and combination protocols.",
      },
      {
        question: "Who is a good candidate for lifting threads?",
        answer: "Ideal candidates have mild to moderate skin laxity, particularly along the jawline, cheeks, or neck. Your nurse will assess your suitability during consultation and may recommend alternative treatments if they would achieve better results for your specific concerns.",
      },
      {
        question: "How much does a thread lift cost in Christchurch?",
        answer: "At Silk Clinical, lifting threads are $2,000 for eight 18g threads. The number of threads your face needs depends on the areas treated and the degree of laxity, so your final plan and quote are confirmed at consultation. If threads are not the right tool for your concern, we will say so and explain the alternatives.",
      },
    ],
    whyChoose: [
      "Registered nurses with advanced thread lifting training.",
      "Premium quality barbed PDO threads.",
      "Thorough assessment to ensure threads are the right treatment for your goals.",
      "Immediate visible lift with progressive collagen improvement.",
      "Comprehensive aftercare and follow-up.",
    ],
    ctaText: "Lifting Threads",
  },
  {
    slug: "prp-vampire-facial",
    title: "PRP Vampire Facial",
    metaTitle: "PRP Vampire Facial Christchurch | Platelet Rich Plasma",
    metaDescription: "PRP vampire facial in Christchurch. Platelet-rich plasma skin rejuvenation by registered nurses for natural collagen stimulation. Book at Silk Clinical.",
    heroSubtitle: "Natural Rejuvenation",
    placeholder: "[PLACEHOLDER: PRP treatment process]",
    overview: "The PRP (Platelet-Rich Plasma) vampire facial uses your own blood platelets to stimulate natural skin rejuvenation. This treatment harnesses your body's healing factors to improve skin texture, tone, and firmness.",
    quickAnswer:
      "The PRP vampire facial in Christchurch at Silk Clinical Aesthetics combines microneedling with platelet-rich plasma from your own blood to stimulate collagen, improve texture, and firm the skin. Treatment takes 60 to 90 minutes including blood preparation and numbing. A series of three sessions spaced 4 to 6 weeks apart is recommended: $600 per session or $1,550 prepaid for three. Results develop over 4 to 6 weeks. Performed by registered nurses.",
    quickFacts: {
      time: "60 to 90 minutes",
      comfort: "Numbing cream applied",
      downtime: "Redness for 24 to 72 hours",
      lasts: "Maintain every 6 to 12 months",
    },
    content: [
      {
        heading: "What is a PRP Vampire Facial?",
        body: "A PRP vampire facial combines microneedling with platelet-rich plasma derived from your own blood. A small blood sample is taken and processed to concentrate the platelets and growth factors. This PRP is then applied to the skin during microneedling, allowing the growth factors to penetrate deep into the skin and stimulate natural healing and collagen production.",
      },
      {
        heading: "How Does PRP Work?",
        body: "Platelets contain growth factors that play a crucial role in tissue repair and regeneration. When concentrated and reintroduced to the skin, these growth factors stimulate fibroblast activity, collagen synthesis, and new blood vessel formation. The microneedling creates channels that allow the PRP to reach the deeper layers of the skin where it can have the greatest effect.",
      },
      {
        heading: "What to Expect",
        body: "Your nurse will draw a small blood sample and process it in a centrifuge to separate the platelet-rich plasma. Numbing cream is applied to the treatment area. The microneedling device then creates micro-channels while the PRP is applied. Treatment takes approximately 60 to 90 minutes including blood preparation and numbing time.",
      },
      {
        heading: "Results and Recovery",
        body: "Expect redness for 24 to 72 hours, similar to sunburn. Some mild swelling and sensitivity is normal. Results develop gradually over 4 to 6 weeks as collagen remodelling occurs. A series of three treatments spaced 4 to 6 weeks apart is recommended for optimal results. Benefits include improved skin texture, reduced fine lines, enhanced tone and radiance, and firmer skin.",
      },
    ],
    pricing: [
      { name: "Vampire Facial Single", price: "$600" },
      { name: "Vampire Facial 3 Sessions", price: "$1,550" },
    ],
    faqs: [
      {
        question: "Is PRP safe?",
        answer: "PRP is derived from your own blood, making allergic reactions or rejection virtually impossible. The procedure is performed in a sterile clinical environment by registered nurses following strict protocols.",
      },
      {
        question: "How many PRP treatments do I need?",
        answer: "We recommend a series of three treatments spaced 4 to 6 weeks apart for optimal results. Maintenance treatments every 6 to 12 months can sustain your results.",
      },
      {
        question: "What is the downtime?",
        answer: "Redness lasts 24 to 72 hours. Most clients return to normal activities within 1 to 2 days. Avoid direct sun, makeup, and active skincare for 24 hours after treatment.",
      },
      {
        question: "Who is a good candidate for PRP?",
        answer: "PRP is suitable for most adults seeking natural skin rejuvenation. It is particularly popular with clients who prefer treatments using their own biological materials. Your nurse will assess your suitability during consultation.",
      },
      {
        question: "Can PRP be combined with other treatments?",
        answer: "Yes. PRP can complement other treatments such as dermal fillers, anti-wrinkle injections, and RF treatments. Your nurse will recommend the best combination and timing for your goals.",
      },
      {
        question: "How much does a PRP vampire facial cost in Christchurch?",
        answer: "A single session is $600, or $1,550 prepaid for the recommended series of three sessions spaced 4 to 6 weeks apart. The treatment takes 60 to 90 minutes including blood preparation and numbing time. Maintenance sessions every 6 to 12 months sustain results; individual results vary.",
      },
    ],
    whyChoose: [
      "Registered nurses performing all PRP treatments.",
      "Sterile clinical environment with strict protocols.",
      "Uses your own growth factors for natural rejuvenation.",
      "Comprehensive treatment plans with follow-up care.",
      "Can be combined with other treatments for enhanced results.",
    ],
    ctaText: "PRP Vampire Facial",
  },
  {
    slug: "prp-hair-loss",
    title: "PRP for Hair Loss",
    metaTitle: "PRP for Hair Loss Christchurch | Hair Rejuvenation",
    metaDescription: "PRP hair rejuvenation in Christchurch. Platelet-rich plasma treatment for hair loss and thinning by registered nurses. Book at Silk Clinical Aesthetics.",
    heroSubtitle: "Hair Rejuvenation",
    placeholder: "[PLACEHOLDER: PRP hair treatment]",
    overview: "PRP for hair loss uses concentrated growth factors from your own blood to stimulate hair follicle activity, promote hair growth, and improve hair thickness and density.",
    quickAnswer:
      "PRP hair rejuvenation in Christchurch at Silk Clinical Aesthetics injects concentrated platelets from your own blood into thinning areas of the scalp to stimulate hair follicles. Treatment takes approximately 45 to 60 minutes. An initial series of three sessions spaced 4 to 6 weeks apart is recommended: $600 per session or $1,550 prepaid for three. Improvements typically show after 2 to 3 months, with full results over 6 to 12 months. Suitable for men and women with early to moderate thinning.",
    quickFacts: {
      time: "45 to 60 minutes",
      comfort: "Mild discomfort, well tolerated",
      downtime: "Scalp tenderness for 24 hours",
      lasts: "Maintain every 6 to 12 months",
    },
    content: [
      {
        heading: "What is PRP for Hair Loss?",
        body: "PRP (Platelet-Rich Plasma) hair rejuvenation involves injecting concentrated platelets from your own blood directly into areas of the scalp experiencing thinning or hair loss. The growth factors in PRP stimulate dormant hair follicles, promote new hair growth, and improve the thickness of existing hair.",
      },
      {
        heading: "How Does PRP Help Hair Loss?",
        body: "The growth factors in PRP increase blood supply to the hair follicle, stimulate the dermal papilla cells responsible for hair growth, extend the growth phase of the hair cycle, and improve hair shaft thickness. This makes PRP effective for both men and women experiencing early to moderate hair thinning.",
      },
      {
        heading: "What to Expect",
        body: "A blood sample is drawn and processed to concentrate the platelets. The PRP is then injected into the scalp in areas of thinning using a fine needle. Treatment takes approximately 45 to 60 minutes. Some mild discomfort may occur during injections, though most clients tolerate the procedure well.",
      },
      {
        heading: "Results and Recovery",
        body: "Hair growth improvements are typically seen after 2 to 3 months, with full results developing over 6 to 12 months. An initial series of three treatments spaced 4 to 6 weeks apart is recommended. Maintenance sessions every 6 to 12 months sustain results. You may experience mild scalp tenderness or redness for 24 hours after treatment.",
      },
    ],
    pricing: [
      { name: "Hair Rejuvenation Single", price: "$600" },
      { name: "Hair Rejuvenation 3 Sessions", price: "$1,550" },
    ],
    faqs: [
      {
        question: "Does PRP really work for hair loss?",
        answer: "Clinical studies show PRP can improve hair density and thickness, particularly in cases of androgenetic alopecia (pattern hair loss). Results vary between individuals. Your nurse will assess your suitability and set realistic expectations during consultation.",
      },
      {
        question: "How many PRP sessions do I need for hair loss?",
        answer: "An initial series of three sessions spaced 4 to 6 weeks apart is recommended. Maintenance treatments every 6 to 12 months help sustain results.",
      },
      {
        question: "Is PRP for hair loss painful?",
        answer: "Most clients experience mild discomfort during scalp injections. The procedure is generally well tolerated. Your nurse can discuss comfort options during your consultation.",
      },
      {
        question: "Who is a good candidate for PRP hair treatment?",
        answer: "PRP works best for early to moderate hair thinning where hair follicles are still present but underactive. It is suitable for both men and women. It is less effective for areas of complete baldness where follicles are no longer present.",
      },
      {
        question: "Can PRP be combined with other hair loss treatments?",
        answer: "Yes. PRP can complement other hair loss treatments and is often used as part of a comprehensive hair restoration plan. Your nurse will discuss all options during your consultation.",
      },
      {
        question: "When will I see results from PRP hair treatment?",
        answer: "Hair growth improvements are typically seen after 2 to 3 months, with full results developing over 6 to 12 months as the hair cycle responds. PRP is not an instant treatment: the growth factors need time to stimulate follicle activity. Your nurse will set a realistic timeline at consultation and review progress across your treatment series.",
      },
    ],
    whyChoose: [
      "Registered nurses performing all PRP treatments.",
      "Suitable for both men and women.",
      "Uses your own growth factors, no synthetic ingredients.",
      "Evidence-based treatment with published clinical support.",
      "Comprehensive assessment and realistic expectation setting.",
    ],
    ctaText: "PRP Hair Rejuvenation",
  },
  {
    slug: "skin-peels",
    title: "Pro Dermal Active Treatments",
    // 63 chars truncated in the SERP, and the brand-name product line led
    // ahead of the term people actually search. Peels first, product second.
    metaTitle: "Clinical Skin Peels Christchurch | Pro Dermal Active",
    metaDescription: "Pro Dermal active treatments in Christchurch. Targeted clinical peels for brightness, clarity, collagen and firmness. Book at Silk Clinical Aesthetics.",
    heroSubtitle: "Skin Renewal",
    placeholder: "[PLACEHOLDER: Pro Dermal active treatment]",
    overview: "Pro Dermal active treatments at Silk Clinical use clinical-grade actives to exfoliate, renew, and improve your skin's tone, texture, and clarity. Four targeted treatments, Bright Skin, Clear Skin, Collagen Booster, and Lift & Firm, are matched to your skin's needs at consultation.",
    quickAnswer:
      "Pro Dermal active treatments in Christchurch at Silk Clinical Aesthetics use clinical-grade active formulations to exfoliate, improve tone and clarity, and reduce pigmentation. Four targeted options are offered, Bright Skin, Clear Skin, Collagen Booster, and Lift & Firm, each $239. Treatment takes 30 to 60 minutes. Mild flaking may occur for 2 to 5 days with more intensive treatments. A series delivers cumulative results.",
    quickFacts: {
      time: "30 to 60 minutes",
      comfort: "Tingling or warming sensation",
      downtime: "Possible flaking for 2 to 5 days",
      lasts: "Cumulative over a series",
    },
    content: [
      {
        heading: "What Are Pro Dermal Active Treatments?",
        body: "Pro Dermal active treatments are professional-strength peel treatments that use clinical-grade active ingredients to remove the outermost layers of dead skin cells, revealing fresher, smoother skin beneath. Each of the four treatments is built around a specific goal: Bright Skin for radiance and pigmentation, Clear Skin for congestion and breakouts, Collagen Booster for fine lines and skin quality, and Lift & Firm for laxity and tone.",
      },
      {
        heading: "How Do They Work?",
        body: "The active ingredients dissolve the bonds between dead skin cells, accelerating natural exfoliation. This stimulates cell turnover, promotes collagen production, and allows newer, healthier skin to surface. Each treatment pairs its actives to the concern it targets, so the formulation working on congestion is not the one working on laxity.",
      },
      {
        heading: "What to Expect",
        body: "Treatment takes 30 to 60 minutes. You may feel a tingling or warming sensation during application. Your clinician will confirm the right treatment for your skin type, concerns, and sensitivity level at consultation.",
      },
      {
        heading: "Results and Recovery",
        body: "Results include improved skin tone and clarity, reduced pigmentation, smoother texture, and a refreshed appearance. Mild peeling or flaking may occur for 2 to 5 days depending on the intensity of the treatment. Sun protection is essential following treatment.",
      },
    ],
    pricing: [
      { name: "Bright Skin", price: "$239" },
      { name: "Clear Skin", price: "$239" },
      { name: "Collagen Booster", price: "$239" },
      { name: "Lift & Firm", price: "$239" },
    ],
    faqs: [
      {
        question: "How often should I have a Pro Dermal treatment?",
        answer: "This depends on the treatment and your skin goals. Most are repeated every 2 to 4 weeks through an active series, with the interval confirmed at consultation. More intensive sessions may need 4 to 6 weeks between treatments.",
      },
      {
        question: "Which treatment is right for me?",
        answer: "Bright Skin targets dullness and pigmentation, Clear Skin targets congestion and breakouts, Collagen Booster targets fine lines and overall skin quality, and Lift & Firm targets laxity and tone. A skin consultation confirms the right starting point and sequence for your skin.",
      },
      {
        question: "What is the downtime?",
        answer: "Minimal for most treatments, with possible slight redness for a few hours. More intensive sessions may cause mild peeling for 2 to 5 days. You can typically return to normal activities the same day.",
      },
      {
        question: "Can I combine a Pro Dermal treatment with other treatments?",
        answer: "Yes. Pro Dermal treatments pair well with microdermabrasion and sonophoresis and can complement other skin treatments. Your clinician will advise on the best combination and sequence for your goals.",
      },
      {
        question: "What results can I expect?",
        answer: "Improved skin clarity, more even tone, reduced pigmentation, smoother texture, and a refreshed appearance. A series of treatments delivers cumulative results.",
      },
      {
        question: "What should I do afterwards?",
        answer: "Sun protection is essential: freshly exfoliated skin is more sensitive to UV, so apply broad-spectrum sunscreen daily and avoid deliberate sun exposure. Expect possible mild peeling or flaking for 2 to 5 days with more intensive treatments, and let it shed naturally rather than picking. You will be given aftercare guidance specific to the treatment you had.",
      },
    ],
    whyChoose: [
      "Clinical-grade actives for effective, controlled exfoliation.",
      "Four targeted treatments matched to your concern, not one generic peel.",
      "Skin assessed at a nurse-led clinic before any treatment is selected.",
      "Can be combined with microdermabrasion and sonophoresis for enhanced results.",
      "An accessible entry point to professional clinical skin treatment.",
    ],
    ctaText: "Pro Dermal Active Treatments",
  },
  {
    slug: "microdermabrasion-sonophoresis",
    title: "Microdermabrasion & Sonophoresis",
    metaTitle: "Microdermabrasion Christchurch | Sonophoresis Facials",
    metaDescription: "Microdermabrasion and sonophoresis in Christchurch. Professional skin exfoliation and enhanced product absorption. Book at Silk Clinical Aesthetics.",
    heroSubtitle: "Exfoliation & Absorption",
    placeholder: "[PLACEHOLDER: Microdermabrasion treatment]",
    overview: "Microdermabrasion combined with sonophoresis provides a two-step skin renewal treatment: professional exfoliation removes dead skin cells, then ultrasound technology enhances the penetration of active ingredients deep into the skin.",
    quickAnswer:
      "Microdermabrasion and sonophoresis in Christchurch at Silk Clinical Aesthetics is a two-step skin renewal treatment: mechanical exfoliation followed by ultrasound-enhanced serum absorption. A face and neck session takes 30 to 45 minutes ($149); the express version takes approximately 20 minutes ($99). There is no downtime, and skin feels smoother immediately. A series every 2 to 4 weeks, then monthly maintenance, delivers the best results.",
    quickFacts: {
      time: "30 to 45 minutes; express 20",
      comfort: "Gentle sanding feel, painless",
      downtime: "None; mild redness for hours",
      lasts: "Maintain monthly",
    },
    content: [
      {
        heading: "What is Microdermabrasion & Sonophoresis?",
        body: "Microdermabrasion uses fine crystals or a diamond-tip handpiece to mechanically exfoliate the outermost layer of skin. Sonophoresis uses low-frequency ultrasound waves to temporarily increase skin permeability, allowing active serums and ingredients to penetrate much deeper than they would with topical application alone. Together, they provide thorough exfoliation followed by enhanced active ingredient delivery.",
      },
      {
        heading: "How Does It Work?",
        body: "The microdermabrasion removes the barrier of dead skin cells, revealing fresh skin beneath and stimulating cell turnover. The sonophoresis then uses ultrasound energy to create temporary micro-pathways in the skin, dramatically increasing the absorption of serums applied immediately after. This means the active ingredients can reach the deeper skin layers where they have the greatest effect.",
      },
      {
        heading: "What to Expect",
        body: "Treatment takes 30 to 45 minutes for a standard session, or approximately 20 minutes for an express session. The microdermabrasion feels like a gentle sanding sensation, and the sonophoresis is painless. No anaesthetic is required. Your skin will feel smooth and fresh immediately after.",
      },
      {
        heading: "Results and Recovery",
        body: "Results include smoother skin texture, improved radiance, enhanced product absorption, and a fresh, healthy glow. There is no downtime, though mild redness may occur for a few hours. For lasting improvement, a series of treatments is recommended.",
      },
    ],
    pricing: [
      { name: "Face & Neck", price: "$149" },
      { name: "Express Face & Neck", price: "$99" },
    ],
    faqs: [
      {
        question: "Is microdermabrasion painful?",
        answer: "No. Most clients find it comfortable, describing a gentle sanding or polishing sensation. No anaesthetic is required.",
      },
      {
        question: "How often should I have microdermabrasion?",
        answer: "Every 2 to 4 weeks for a series of treatments, then monthly maintenance. Your nurse will recommend a schedule based on your skin.",
      },
      {
        question: "What does sonophoresis add to the treatment?",
        answer: "Sonophoresis increases active ingredient absorption by up to 20 times compared to topical application alone, making your treatment serums significantly more effective.",
      },
      {
        question: "Is there any downtime?",
        answer: "No. You can return to normal activities immediately. Mild redness may last a few hours. Sun protection is recommended.",
      },
      {
        question: "Who is a good candidate?",
        answer: "Most skin types benefit from microdermabrasion. It is excellent for dull skin, congestion, uneven texture, and fine lines. Your nurse will assess your suitability during consultation.",
      },
      {
        question: "How much does microdermabrasion cost in Christchurch?",
        answer: "A face and neck session with sonophoresis is $149. The express microdermabrasion option is $99 per session. The full session takes 30 to 45 minutes and the express version approximately 20 minutes, so it fits inside a lunch break.",
      },
    ],
    whyChoose: [
      "Dual-action treatment for comprehensive skin renewal.",
      "Enhanced product absorption through sonophoresis technology.",
      "No downtime, suitable for busy schedules.",
      "Same-day visible smoothing with no downtime.",
      "Can be combined with skin peels for enhanced exfoliation.",
    ],
    ctaText: "Microdermabrasion & Sonophoresis",
  },
  {
    slug: "microneedling",
    title: "Microneedling",
    metaTitle: "Microneedling Christchurch | Collagen Induction Therapy",
    metaDescription: "Microneedling in Christchurch at Silk Clinical Aesthetics: collagen induction therapy to refine texture, pores, fine lines and scarring. Single sessions or a prepaid course of three, nurse-led.",
    heroSubtitle: "Collagen Induction",
    placeholder: "[PLACEHOLDER: Microneedling treatment]",
    overview: "Microneedling, also known as collagen induction therapy, uses a device of very fine needles to create controlled micro-channels in the skin. These tiny, precise injuries prompt the skin's own repair response, building fresh collagen and elastin to refine texture, soften fine lines, and improve tone over a course of treatments.",
    quickAnswer:
      "Microneedling in Christchurch at Silk Clinical Aesthetics is a collagen induction treatment that uses fine needles to create controlled micro-channels, prompting the skin to build new collagen and elastin. It helps with texture, enlarged pores, fine lines, dullness, and mild scarring. A full-face session takes 30 to 45 minutes plus numbing time, with redness for 24 to 48 hours. Full face is $349 a session, or $900 prepaid for three; face and neck is $450. A course of three, spaced 4 to 6 weeks apart, gives the best result.",
    quickFacts: {
      time: "30 to 45 minutes plus numbing",
      comfort: "Numbing cream; mild prickling",
      downtime: "Redness for 24 to 48 hours",
      lasts: "Cumulative; maintain periodically",
    },
    content: [
      {
        heading: "What is Microneedling?",
        body: "Microneedling, or collagen induction therapy, uses a precision device fitted with very fine needles to create thousands of controlled micro-channels in the skin. These are small enough to heal quickly, yet enough to switch on the skin's natural repair process. At Silk Clinical, the depth and technique are matched to your skin and your concern, whether that is overall texture, pores, early lines, or scarring.",
      },
      {
        heading: "How Does Microneedling Work?",
        body: "Each micro-channel triggers the skin's wound-healing response, stimulating the production of new collagen and elastin, the fibres that keep skin firm and smooth. As the skin remodels over the following weeks, texture refines, pores appear smaller, and tone becomes more even. The channels also allow serums applied during and after treatment to absorb more effectively.",
      },
      {
        heading: "What to Expect",
        body: "We begin with a thorough cleanse and apply a numbing cream, left on for around 20 to 30 minutes so the treatment stays comfortable. The device is then passed evenly over the face, and over the neck as well if that area is included. A full-face session takes about 30 to 45 minutes. You may feel a light prickling or vibration and some warmth. Afterwards the skin looks flushed, similar to mild sunburn.",
      },
      {
        heading: "Results and Recovery",
        body: "Redness and a warm, tight feeling usually settle within 24 to 48 hours, sometimes with light flaking over a few days. A fresh glow appears within the first week, while the collagen-building results continue to develop over several weeks. Because the effect is cumulative, a course of three sessions spaced 4 to 6 weeks apart is recommended for texture, pores, and scarring, followed by periodic maintenance. Daily sun protection is essential while the skin renews.",
      },
    ],
    pricing: [
      { name: "Full Face, 3 Session Prepay", price: "$900", note: "Best value: a full course of three" },
      { name: "Full Face, Single Session", price: "$349" },
      { name: "Face & Neck, Single Session", price: "$450" },
    ],
    faqs: [
      {
        question: "Is microneedling painful?",
        answer: "A numbing cream is applied before we begin and left to work for around 20 to 30 minutes, so most clients find the treatment comfortable, describing a light prickling or vibrating sensation rather than pain.",
      },
      {
        question: "How many sessions will I need?",
        answer: "A single session gives skin a healthy refresh. For texture, enlarged pores, fine lines, or scarring, a course of three sessions spaced 4 to 6 weeks apart works best, since collagen builds cumulatively. Periodic maintenance keeps the results going. Your clinician will recommend a plan at consultation.",
      },
      {
        question: "What is the downtime?",
        answer: "Expect redness like mild sunburn for 24 to 48 hours, sometimes with slight tightness, dryness, or light flaking over a few days. Most people return to their routine the next day. We recommend leaving makeup off for 24 hours and wearing daily SPF while the skin recovers.",
      },
      {
        question: "What can microneedling help with?",
        answer: "Microneedling is well suited to uneven texture, enlarged pores, dullness, early fine lines, and mild scarring, including some acne scarring, as well as overall tone. During consultation we will assess your skin and be honest about what microneedling can and cannot achieve for your concern.",
      },
      {
        question: "Can microneedling be combined with other treatments?",
        answer: "Yes. Microneedling pairs well with professional skin peels and with PRP, where your own platelet-rich plasma is applied during the treatment (our Vampire Facial). Your clinician will advise on the best sequence and spacing for your goals.",
      },
      {
        question: "What should I do after microneedling?",
        answer: "Keep the skin clean and hydrated, avoid makeup for 24 hours, and pause active ingredients such as retinol, vitamin C, and acids for 3 to 5 days. Avoid heat, saunas, and strenuous exercise for 24 to 48 hours, do not pick or exfoliate, and wear a broad-spectrum SPF daily. You will be given personalised aftercare to take home.",
      },
      {
        question: "How much does microneedling cost in Christchurch?",
        answer: "A full-face session is $349, or $900 when you prepay for a course of three, which is the best value. A face and neck session is $450. Pricing is confirmed at consultation before any treatment.",
      },
    ],
    whyChoose: [
      "Collagen induction that works with your skin's own repair process, not against it.",
      "A considered assessment first, with honest guidance on what microneedling can and cannot do for your skin.",
      "Single sessions for a refresh, or a prepaid course of three for texture, pores, and scarring.",
      "Depth and technique matched to your skin, face and neck options available.",
      "A calm, nurse-led clinic focused on long-term skin health rather than quick fixes.",
    ],
    ctaText: "Microneedling",
  },
  {
    slug: "fat-dissolver",
    title: "Fat Dissolver",
    metaTitle: "Fat Dissolver Injections Christchurch | Double Chin",
    metaDescription: "Fat dissolver injections in Christchurch for double chin and localised fat. Non-surgical fat reduction by registered nurses at Silk Clinical Aesthetics.",
    heroSubtitle: "Non-Surgical Fat Reduction",
    placeholder: "[PLACEHOLDER: Fat dissolver treatment area]",
    overview: "Fat dissolver injections offer a non-surgical solution for reducing stubborn pockets of fat, particularly under the chin (submental fat). The treatment permanently destroys fat cells in the targeted area for a more defined profile.",
    quickAnswer:
      "Fat dissolver injections in Christchurch at Silk Clinical Aesthetics use a synthetic form of deoxycholic acid to permanently destroy fat cells in small, stubborn deposits, most commonly under the chin. Treatment takes approximately 15 to 30 minutes, with significant swelling expected for 3 to 7 days afterwards. Most clients need 2 to 4 sessions spaced 6 to 8 weeks apart. Once achieved, results are permanent, since treated fat cells do not regenerate. Pricing is confirmed at consultation.",
    quickFacts: {
      time: "15 to 30 minutes",
      comfort: "Stinging during injection",
      downtime: "Swelling for 3 to 7 days",
      lasts: "Permanent in treated areas",
    },
    content: [
      {
        heading: "What is Fat Dissolver?",
        body: "Fat dissolver injections use a synthetic form of deoxycholic acid, a substance that naturally occurs in the body and aids in the breakdown of dietary fat. When injected into targeted fat deposits, it destroys the fat cell membrane, causing the fat cells to break down and be naturally processed by the body.",
      },
      {
        heading: "How Does Fat Dissolver Work?",
        body: "The active ingredient is injected directly into the fat deposit using a series of small injections. It disrupts the fat cell membrane, causing the cell to release its contents. The body then naturally processes and eliminates the destroyed fat cells over the following weeks. Because the fat cells are permanently destroyed, results are long-lasting.",
      },
      {
        heading: "What to Expect",
        body: "Treatment takes approximately 15 to 30 minutes. Your nurse will mark the treatment area and administer multiple small injections. Some discomfort, burning, or stinging is common during injection. Swelling is significant and expected, typically lasting 3 to 7 days. Multiple treatments (usually 2 to 4 sessions spaced 6 to 8 weeks apart) are typically needed for optimal results.",
      },
      {
        heading: "Results and Recovery",
        body: "Results develop gradually as the body processes the destroyed fat cells. Noticeable improvement is typically seen after 2 to 3 treatments. Swelling, tenderness, and firmness in the treated area are normal and expected for several days after each session. Once results are achieved, they are permanent as the treated fat cells do not regenerate.",
      },
    ],
    pricing: [],
    faqs: [
      {
        question: "How many fat dissolver treatments do I need?",
        answer: "Most clients require 2 to 4 treatments spaced 6 to 8 weeks apart. The number depends on the amount of fat in the treatment area and your desired outcome.",
      },
      {
        question: "Is fat dissolver permanent?",
        answer: "Yes. The treated fat cells are permanently destroyed and do not regenerate. However, remaining fat cells can enlarge with significant weight gain, so maintaining a stable weight is recommended.",
      },
      {
        question: "What is the recovery like?",
        answer: "Expect significant swelling for 3 to 7 days. Tenderness, firmness, and possible bruising are normal. Most clients find the swelling manageable but prefer to schedule treatments when they have a few days without important social events.",
      },
      {
        question: "What areas can be treated?",
        answer: "The most common area is under the chin (double chin). It can also be used for other small, localised fat deposits. Your nurse will assess whether fat dissolver is appropriate for your specific concerns.",
      },
      {
        question: "Is fat dissolver painful?",
        answer: "You may feel burning or stinging during injection, which subsides within minutes. The swelling and tenderness after treatment can be uncomfortable for a few days. Your nurse will discuss pain management options.",
      },
      {
        question: "Who is not suitable for fat dissolver?",
        answer: "Fat dissolver treats localised fat, not loose skin. If skin laxity rather than fat volume is the main cause of the concern, dissolving fat can make laxity more visible, and we will recommend a skin-tightening treatment instead. It is also not recommended during pregnancy or breastfeeding. Suitability is assessed honestly at consultation before any treatment is booked.",
      },
    ],
    whyChoose: [
      "Registered nurses with training in fat dissolver injections.",
      "Thorough assessment to determine suitability.",
      "Permanent fat reduction in treated areas.",
      "Non-surgical alternative to liposuction for small fat deposits.",
      "Comprehensive aftercare and follow-up.",
    ],
    ctaText: "Fat Dissolver",
  },
  {
    slug: "combined-treatment-plans",
    title: "Combined Treatment Plans",
    metaTitle: "Combined Treatment Plans Christchurch | Multi-Modality",
    metaDescription: "Personalised combined treatment plans in Christchurch. Tailored combination of cosmetic injectables and skin treatments for comprehensive results. Silk Clinical.",
    heroSubtitle: "Personalised Plans",
    placeholder: "[PLACEHOLDER: Treatment consultation]",
    overview: "Many of the best aesthetic outcomes are achieved through combining complementary treatments. Our registered nurses create personalised treatment plans that address multiple concerns with a strategic, phased approach.",
    quickAnswer:
      "Combined treatment plans at Silk Clinical Aesthetics in Christchurch pair complementary treatments, such as Volnewmer RF with Ultraformer MPT HIFU, or anti-wrinkle injections with dermal fillers, to address concerns no single treatment covers alone. Your registered nurse builds the plan at consultation, sequences each treatment with appropriate healing intervals, and reviews progress as you go. A plan might span 3 to 12 months depending on the treatments involved.",
    content: [
      {
        heading: "Why Combine Treatments?",
        body: "No single treatment addresses every aspect of facial ageing or skin quality. Combining treatments allows us to target different concerns simultaneously or sequentially: for example, using HIFU for deep tissue tightening, Volnewmer RF for collagen remodelling, and injectables for volume restoration and line reduction. The result is more comprehensive than any single treatment alone.",
      },
      {
        heading: "How We Create Your Plan",
        body: "Your journey begins with a comprehensive consultation. Your registered nurse will assess your skin, discuss your goals, and explain which treatments will deliver the best results for your specific concerns. We then create a phased treatment plan with recommended timing between different treatments, expected outcomes at each stage, and a realistic timeline for achieving your goals.",
      },
      {
        heading: "Popular Combinations",
        body: "Common combination approaches include Volnewmer RF + Ultraformer MPT for comprehensive non-surgical tightening, anti-wrinkle injections + dermal fillers for complete facial rejuvenation, skin boosters + cosmetic injectables for hydration and volume, and thread lifts + fillers for lifting and volumising. Each plan is tailored to you, so your combination may differ from these examples.",
      },
      {
        heading: "What to Expect",
        body: "Combined treatment plans are delivered over multiple appointments, with appropriate intervals between different treatments to allow healing and optimise results. Your nurse will coordinate the timing of each treatment and adjust your plan as your skin responds. Regular reviews ensure we are on track to achieve your goals.",
      },
    ],
    pricing: [],
    faqs: [
      {
        question: "How do I know which treatments to combine?",
        answer: "This is determined during your consultation. Your nurse will assess your concerns, explain the options, and recommend the combination that will deliver the best results for your specific goals and budget.",
      },
      {
        question: "Is it safe to have multiple treatments?",
        answer: "Yes, when properly planned and spaced. Your registered nurse will ensure appropriate intervals between treatments and monitor your progress throughout. Combining treatments is common practice in aesthetic medicine.",
      },
      {
        question: "Are combined plans more expensive?",
        answer: "Combined plans involve multiple treatments, so the total investment is higher than a single treatment. However, many clients find that a strategic combination delivers significantly better results than spending the same amount on a single treatment type. We can work with your budget to prioritise treatments.",
      },
      {
        question: "How long does a combined treatment plan take?",
        answer: "This depends on the treatments involved. A plan might span 3 to 12 months, with appointments scheduled at optimal intervals. Your nurse will outline the expected timeline during consultation.",
      },
      {
        question: "Can I start with one treatment and add more later?",
        answer: "Absolutely. Many clients begin with a single treatment and then add complementary treatments as they see results and develop their aesthetic goals. Your plan can evolve with you.",
      },
      {
        question: "In what order should combined treatments be done?",
        answer: "The assessment decides the order. Different treatments work at different tissue depths and have different healing windows, so sequencing matters, and it depends on your skin, your goals, and which treatments the plan involves. Your registered nurse coordinates the timing of each step and adjusts the plan as your skin responds, rather than following a fixed template.",
      },
    ],
    whyChoose: [
      "Registered nurses who understand how different treatments complement each other.",
      "Comprehensive consultation and personalised planning.",
      "Strategic timing to optimise results from each treatment.",
      "Ongoing monitoring and plan adjustment.",
      "Access to a full range of advanced treatments under one roof.",
    ],
    ctaText: "Combined Treatment Plan",
  },
];

export function getTreatmentBySlug(slug: string): Treatment | undefined {
  return treatments.find((t) => t.slug === slug);
}

export function getAllTreatmentSlugs(): string[] {
  return treatments.map((t) => t.slug);
}

/**
 * Lowest numeric price across a treatment's pricing rows, returned as an
 * unformatted NZD string ("180" / "350"). Kept for display contexts that
 * genuinely want the lowest line item. Do NOT use it for structured data:
 * see `treatmentOfferPricing` below for the misleading-price trap.
 */
export function treatmentPriceFrom(treatment: Treatment): string | undefined {
  const numbers = treatment.pricing
    .map((p) => Number(p.price.replace(/[^0-9.]/g, "")))
    .filter((n) => Number.isFinite(n) && n > 0);
  if (numbers.length === 0) return undefined;
  return Math.min(...numbers).toFixed(0);
}

// ---------------------------------------------------------------------------
// Structured-data pricing (single source of truth for JSON-LD offers)
// ---------------------------------------------------------------------------

/** A per-unit line item, e.g. "Dysport per unit" at $6.00. */
export type TreatmentUnitPrice = {
  /** Product or item name with the unit suffix stripped, e.g. "Dysport". */
  name: string;
  /** Unformatted NZD amount, e.g. "6". */
  price: string;
  /** The unit the price is quoted per, e.g. "unit" or "ml". */
  unitText: string;
};

export type TreatmentOfferPricing = {
  /** Lowest amount a client can actually be charged, unformatted NZD. */
  minPrice: string;
  /** Plain-English qualifier shown with the minimum, where one applies. */
  minPriceDescription?: string;
  /** Per-unit rows, emitted as UnitPriceSpecification rather than a price. */
  unitPrices?: TreatmentUnitPrice[];
};

/** "Dysport per unit", "Tear Trough per ml": priced per unit, not bookable alone. */
const UNIT_ROW = /^(.*?)\s+per\s+(unit|ml)$/i;
/** Rows that only exist as an extra on top of another treatment. */
const ADD_ON_ROW = /add-on only/i;
/** A stated floor charge for the visit, e.g. "Minimum toxin charge". */
const MINIMUM_ROW = /^minimum\b/i;

const toAmount = (price: string) => Number(price.replace(/[^0-9.]/g, ""));

/**
 * Offer pricing for JSON-LD. The naive "lowest row wins" derivation is a
 * misleading-price bug: anti-wrinkle injections would advertise $6, the
 * price of a single Dysport unit, which nobody can buy. So rows that cannot
 * be purchased on their own (per-unit / per-ml line items, and add-on-only
 * extras) are excluded from the minimum, and per-unit rows are surfaced
 * separately as UnitPriceSpecification entries. Where the treatment states
 * its own floor charge ("Minimum toxin charge"), that row is the minimum.
 */
export function treatmentOfferPricing(
  treatment: Treatment
): TreatmentOfferPricing | undefined {
  const unitPrices: TreatmentUnitPrice[] = [];
  const bookable: number[] = [];
  let statedMinimum: number | undefined;

  for (const row of treatment.pricing) {
    const amount = toAmount(row.price);
    if (!Number.isFinite(amount) || amount <= 0) continue;

    const unitMatch = row.name.match(UNIT_ROW);
    if (unitMatch) {
      unitPrices.push({
        name: unitMatch[1],
        price: String(amount),
        unitText: unitMatch[2].toLowerCase(),
      });
      continue;
    }
    if (MINIMUM_ROW.test(row.name)) {
      statedMinimum =
        statedMinimum === undefined ? amount : Math.min(statedMinimum, amount);
      continue;
    }
    if (ADD_ON_ROW.test(row.name)) continue;
    bookable.push(amount);
  }

  const minPrice =
    statedMinimum ?? (bookable.length > 0 ? Math.min(...bookable) : undefined);
  if (minPrice === undefined) return undefined;

  return {
    minPrice: minPrice.toFixed(0),
    ...(unitPrices.length > 0 && {
      minPriceDescription:
        "Minimum charge per visit. Units cannot be purchased individually.",
      unitPrices,
    }),
  };
}

/** Offer pricing by slug, for catalogue-level structured data. */
export function treatmentOfferPricingBySlug(
  slug: string
): TreatmentOfferPricing | undefined {
  const treatment = getTreatmentBySlug(slug);
  return treatment ? treatmentOfferPricing(treatment) : undefined;
}
